import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Plus, Trash, Printer, Funnel, CheckCircle, Lock, Broadcast, EyeSlash, FloppyDisk, UploadSimple, PencilSimple, ArrowsLeftRight, Warning } from "@phosphor-icons/react";
import TradeRequestDialog from "@/components/TradeRequestDialog";
import PublishWeeksDialog from "@/components/PublishWeeksDialog";
import { ScheduleHealthButton, ScheduleHealthDialog, useScheduleHealth } from "@/components/ScheduleHealth";

const AMBULANCE_ROLES = ["MVO", "Head Tech", "Crew 1", "Crew 2"];
// Special Events reuse the ambulance seats; "EMT" is the Head Tech seat (same eligibility).
const PAST_PICKUP_MSG = "This tour has already started — past shifts can't be picked up. Contact a supervisor if you worked it.";
const isSpecialEventPos = (p) => /^SPECIAL EVENT\b/i.test(String(p || "").trim());
const roleLabel = (role, position) => (role === "Head Tech" && isSpecialEventPos(position) ? "EMT" : role);
const EVENT_CREW_SEATS = [["MVO", "MVO"], ["Head Tech", "EMT"], ["Crew 1", "Crew 1"], ["Crew 2", "Crew 2"], ["", "General staff"]];
const ROLE_COLOR = {
  "MVO": "bg-orange-600 text-white",
  "Head Tech": "bg-purple-700 text-white",
  "Crew 1": "bg-teal-600 text-white",
  "Crew 2": "bg-teal-700 text-white",
};

const CERT_COLOR = {
  Trainee: "bg-slate-500 text-white",
  Driver: "bg-amber-600 text-white",
  EMT: "bg-blue-600 text-white",
  Paramedic: "bg-purple-600 text-white",
  Supervisor: "bg-slate-900 text-white",
  "CPR Instructor": "bg-teal-600 text-white",
};

// All schedule days are represented as JS Date objects anchored at
// 12:00 UTC on the intended Eastern-Time calendar date. This makes
// `_etParts(d).date` return the exact YYYY-MM-DD we want, in every
// browser timezone, and lets us format labels safely with
// `timeZone: "UTC"` since we control the underlying instant.
function etTodayYMD() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(new Date()); // "YYYY-MM-DD"
}
function ymdToUtcNoon(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}
function startOfWeek(d) {
  // Accept a Date (interpreted via its ET calendar date) or a YYYY-MM-DD
  // string. Rewind to the Sunday of that ET week.
  const ymd = typeof d === "string" ? d : (_etParts(d.toISOString())?.date || etTodayYMD());
  const base = ymdToUtcNoon(ymd);
  base.setUTCDate(base.getUTCDate() - base.getUTCDay());
  return base;
}
function addDays(d, n) { const c = new Date(d); c.setUTCDate(c.getUTCDate() + n); return c; }
function fmtDay(d) { return d.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" }); }
function fmtMon(d) { return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }); }
// 24-hour HH:MM — anchored to Eastern Time (BSVAC's home tz). Every
// display in the schedule uses this so a shift posted "22:00" reads as
// "22:00" for a reader in any timezone, and the Tour-1 previous-day
// banner + sort key look at the SAME hour the operator posted.
const _SCHED_ET_PARTS_CACHE = new Map();
function _etParts(iso) {
  if (_SCHED_ET_PARTS_CACHE.has(iso)) return _SCHED_ET_PARTS_CACHE.get(iso);
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
    weekday: "long",
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  // Intl returns "24" for midnight on some browsers — normalize to "00".
  if (p.hour === "24") p.hour = "00";
  // Compute day-of-week 0..6 (Sun..Sat) from the ET calendar date, not
  // the browser-local Date. `new Date("YYYY-MM-DD")` parses as UTC
  // midnight; UTC midnight's getUTCDay() is the calendar-date's weekday,
  // which is exactly what we want (independent of the reader's TZ).
  const _dowDate = new Date(`${p.year}-${p.month}-${p.day}T00:00:00Z`);
  const out = {
    hour: p.hour,
    minute: p.minute,
    date: `${p.year}-${p.month}-${p.day}`, // YYYY-MM-DD in ET
    weekday: p.weekday,
    hourNum: parseInt(p.hour, 10),
    minuteNum: parseInt(p.minute, 10),
    dowNum: _dowDate.getUTCDay(),  // 0=Sun..6=Sat, ET calendar
  };
  _SCHED_ET_PARTS_CACHE.set(iso, out);
  return out;
}
function fmtTime24(iso) {
  const p = _etParts(iso);
  return p ? `${p.hour}:${p.minute}` : "--:--";
}
function etDayKey(iso) {
  // "YYYY-MM-DD" in ET — used to place shifts under the correct day
  // column so a 22:00-Friday shift doesn't slip into Saturday for a
  // UTC-side reader.
  const p = _etParts(iso);
  return p ? p.date : "";
}
function etHour(iso) {
  const p = _etParts(iso);
  return p ? p.hourNum : 0;
}
function fmtDateLong(d) { return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
function tourHours(t) { return Math.max(0, (new Date(t.end_time) - new Date(t.start_time)) / 3600000); }
function isoDatePart(d) { return d.toISOString().split("T")[0]; }

// TOUR 1 shifts run overnight. The night-side start hours (22, 23) must
// always sort before the morning-side hours (00, 01, 02, …). We rotate the
// clock so 22:00 becomes the anchor: rotated = (hour + 2) mod 24.
//   22 → 0, 23 → 1, 00 → 2, 01 → 3, 02 → 4, …, 21 → 23
// After rotation a normal ascending sort produces the requested order.
function isTourOne(position) {
  return String(position || "").trim().toUpperCase().startsWith("TOUR 1");
}
function tour1SortKey(iso) {
  // Rotate the ET clock: 22 → 0, 23 → 1, 00 → 2, 01 → 3, …
  const p = _etParts(iso);
  const h = p ? p.hourNum : 0;
  const m = p ? p.minuteNum : 0;
  const rotated = (h + 2) % 24;
  return rotated * 60 + m;
}
function sortForPosition(position) {
  if (!isTourOne(position)) {
    return (a, b) => a.start_time.localeCompare(b.start_time);
  }
  return (a, b) => {
    const ka = tour1SortKey(a.start_time);
    const kb = tour1SortKey(b.start_time);
    if (ka !== kb) return ka - kb;
    // Tie-break on the raw start_time so equal-hour shifts stay stable.
    return a.start_time.localeCompare(b.start_time);
  };
}

// TOUR 1 shifts posted with a 22:00 or 23:00 start actually require the
// member to report the evening BEFORE the day the shift is filed under.
// We compute a "starts on {previous weekday}" banner label so the grid
// makes that obvious. All hour/day math is ET-anchored.
function previousDayBanner(position, startIso) {
  if (!isTourOne(position)) return null;
  const p = _etParts(startIso);
  if (!p) return null;
  if (p.hourNum !== 22 && p.hourNum !== 23) return null;
  // Compute the ET day BEFORE the posted date. Parse the ET yyyy-mm-dd
  // as a Date at noon UTC to sidestep DST edges, then subtract 1 day.
  const [y, mo, dy] = p.date.split("-").map(Number);
  const base = new Date(Date.UTC(y, mo - 1, dy, 12));
  base.setUTCDate(base.getUTCDate() - 1);
  const prevWeekday = base.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
  return `TOUR STARTS ${prevWeekday.toUpperCase()}`;
}

// Accept either "HHMM" or "HH:MM" and return a normalized "HH:MM" value.
// Auto-inserts the colon after the second digit so users typing "1800" get
// "18:00" without a manual colon keystroke. Only digits are kept.
function normalizeTimeInput(value) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}
function combineDateTime(dateStr, timeStr) {
  // dateStr "YYYY-MM-DD", timeStr "HH:MM" — treated as *Eastern Time*
  // (BSVAC's home tz) regardless of the operator's browser zone. We
  // build a UTC anchor first (Date.UTC), then figure out how far ET was
  // from UTC at that moment (DST-aware via Intl) and shift back so
  // toISOString() prints the correct instant.
  //
  // Without this ET-anchoring, a 00:00 shift entered from a UTC+X
  // browser would land on the wrong ET calendar date on the grid.
  if (!dateStr || !/^\d{2}:\d{2}$/.test(timeStr)) return null;
  const [y, mo, dy] = dateStr.split("-").map((n) => parseInt(n, 10));
  const [h, m] = timeStr.split(":").map((n) => parseInt(n, 10));
  if (!y || !mo || !dy || h > 23 || m > 59) return null;
  // Provisional: treat "YYYY-MM-DD HH:MM" as UTC.
  const provisional = new Date(Date.UTC(y, mo - 1, dy, h, m, 0));
  // How far off is ET from UTC at that instant?
  const parts = _etParts(provisional.toISOString());
  const [py, pm, pd] = (parts?.date || `${y}-${String(mo).padStart(2,"0")}-${String(dy).padStart(2,"0")}`).split("-").map((n) => parseInt(n, 10));
  const ph = parts ? parts.hourNum : h;
  const pmin = parts ? parts.minuteNum : m;
  // ET wall time as a UTC instant
  const etAsUtc = Date.UTC(py, pm - 1, pd, ph, pmin, 0);
  // Difference in ms between provisional UTC and ET-wall-as-UTC = offset
  const offsetMs = provisional.getTime() - etAsUtc;
  return new Date(provisional.getTime() + offsetMs);
}

function userIsAuthorized(user, requiredCert) {
  if (!user) return false;
  if (!requiredCert) return true;
  const req = String(requiredCert).trim();
  const lc = req.toLowerCase();
  // iter26 — Volunteer and Training are open to every active member.
  if (lc === "volunteer" || lc === "training") return user.active !== false;
  // iter22/26 — Communications aliases + Dispatcher gate on dispatcher_status.
  if (lc === "dispatcher" || lc === "communications" || lc === "comms") {
    return (user.dispatcher_status || "").trim().toLowerCase() === "cleared";
  }
  // iter25/27/28 — Legacy required_certification values that mean MVO
  // driver clearance come in several spellings. Treat all as driver,
  // accepting clearance date OR vehicle authorization list.
  // Accept any legacy spelling that contains "mvo" or "driv" (covers
  // "Driver", "Driving", "MVO (BLS)", "Driver's License", etc.).
  if (lc.includes("mvo") || lc.includes("driv")) {
    const dateOK = !!(user.driver_clearance_date || "").toString().trim();
    const vehicles = Array.isArray(user.cleared_to_drive) ? user.cleared_to_drive.filter((v) => String(v || "").trim()) : [];
    return dateOK || vehicles.length > 0;
  }
  // iter26 — Logistics gates on the Logistics authorized cert.
  if (lc === "logistics") {
    const certs = (user.certifications || []).map((c) => String(c).trim().toLowerCase());
    return certs.includes("logistics");
  }
  const list = (user.certifications || (user.certification ? [user.certification] : [])).map((c) => String(c).trim());
  if (list.some((c) => c.toLowerCase() === lc)) return true;
  // Deliberately do NOT fall back to ems_certification here — ambulance
  // seats go through the ambulance_role branch, which is clearance-driven.
  return false;
}

// Combined eligibility — matches the backend `_can_pickup(user, required,
// ambulance_role)` precedence rule: when a shift has an `ambulance_role`
// the role's eligibility map is the authoritative gate (so Interns listed
// under Crew 1 / Crew 2 can staff the seat even if `required_certification`
// is EMT). When no role is set, falls back to the cert match.
function userCanPickupShift(user, tour, roleEligibility) {
  if (!user) return false;
  const role = tour?.ambulance_role;
  if (role) return userEligibleForRole(user, role, roleEligibility);
  return userIsAuthorized(user, tour?.required_certification);
}

function userEligibleForRole(user, role, roleEligibility) {
  if (!user || !role) return true;
  // iter26 — Eligibility is driven purely by profile clearances, not
  // by the member's EMS credential. Mirrors
  // `user_helpers.is_user_eligible_for_role` on the backend verbatim.
  const certs = (user.certifications || (user.certification ? [user.certification] : [])).map((c) => String(c).trim());
  const hasCert = (c) => certs.some((x) => x.toLowerCase() === c.toLowerCase());
  const clearance = (user.clearance_status || "N").trim() || "N";
  // iter28 — Mirror backend `_is_cleared_driver`: accept the clearance
  // date OR a non-empty vehicle authorization list. Prevents a stale
  // field from blocking a driver who was cleared in the UI.
  const vehicles = Array.isArray(user.cleared_to_drive) ? user.cleared_to_drive.filter((v) => String(v || "").trim()) : [];
  const isDriver = !!(user.driver_clearance_date || "").toString().trim() || vehicles.length > 0;
  const isDispatcher = (user.dispatcher_status || "").trim().toLowerCase() === "cleared";
  const r = role.trim();
  const rLower = r.toLowerCase();
  if (r === "MVO") return isDriver;
  if (r === "Head Tech") return ["C", "C+", "FTO"].includes(clearance);
  if (r === "Crew 1" || r === "Crew 2") {
    if (["I", "C", "C+", "FTO"].includes(clearance)) return true;
    if (isDriver) return true;
    if (hasCert("Student")) return true;
    return false;
  }
  if (rLower === "communications" || rLower === "dispatcher" || rLower === "comms") return isDispatcher;
  if (r === "Logistics") return hasCert("Logistics");
  if (rLower === "volunteer" || rLower === "training") return true;
  // Unknown ambulance roles — fall back to the legacy map if present.
  const eligible = (roleEligibility && roleEligibility[r]) || [];
  if (!eligible.length) return true;
  return certs.some((c) => eligible.includes(c));
}

// Driver Restriction — mirrors backend `restriction_overlaps_tour`.
// Returns true when the user has an active restriction and the tour is
// an MVO slot that overlaps the restriction window. Supervisors cannot
// assign a restricted member to an overlapping MVO tour (no override).
function restrictionBlocksMVO(user, tour) {
  if (!user || !tour) return false;
  if ((tour.ambulance_role || "") !== "MVO") return false;
  const r = user.driver_restriction;
  if (!r || !r.active) return false;
  try {
    const ts = new Date(tour.start_time).getTime();
    const te = new Date(tour.end_time).getTime();
    const rs = r.start_date ? new Date(r.start_date + "T00:00:00Z").getTime() : Date.now();
    const re = r.end_date ? (new Date(r.end_date + "T00:00:00Z").getTime() + 86400000) : null;
    if (re && te <= rs) return false;
    if (re && ts >= re) return false;
    return true;
  } catch {
    return true;
  }
}

// ===== Shift block =====
// Each row is just the role name as a clickable text link. No background,
// no border, no cert badge. Open slots show "Unassigned" in yellow next to
// the role name. The drawer still enforces cert + role validation when a
// user actually attempts to pick up the slot.
function ShiftBlock({ group, currentUser, onClick }) {
  const tours = group.tours;
  const mineHere = tours.find((t) => t.assigned_user_id === currentUser?.id);
  const anyDropPending = tours.some((t) => t.status === "drop_pending");
  const openSlots = tours.filter((t) => !t.assigned_user_id && t.status === "open");
  const draftHere = tours.some((t) => t.published === false);
  // Spec #9 — show clearance code (N/I/C/C+/FTO) in parens next to each
  // assignee's name on the schedule tile. The code is enriched onto
  // each tour row by the backend list endpoint and already reflects
  // the auto C→C+ promotion.
  const assignees = tours
    .filter((t) => t.assigned_user_name)
    .map((t) => {
      const code = t.assigned_user_clearance;
      return code ? `${t.assigned_user_name} (${code})` : t.assigned_user_name;
    });
  const allOpen = openSlots.length === tours.length;

  let textCls = "text-slate-700 hover:text-slate-900";
  if (mineHere) textCls = "text-emerald-700 font-bold hover:text-emerald-800";
  else if (anyDropPending) textCls = "text-amber-800 hover:text-amber-900";
  else if (allOpen) textCls = "text-slate-900 hover:text-slate-700";
  else textCls = "text-blue-700 hover:text-blue-900";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`shift-${group.key}`}
      className={`w-full text-left px-2 h-[28px] flex items-center gap-1.5 hover:underline transition-colors ${textCls}`}
    >
      <span className="text-[12px] font-semibold truncate">
        {roleLabel(group.ambulance_role, group.position) || "Tour"}
      </span>
      {mineHere ? (
        <span className="text-[11px] font-bold text-emerald-700 truncate">— YOU</span>
      ) : assignees.length === 0 ? (
        <span
          className="text-[10px] font-bold uppercase tracking-tight bg-yellow-300 text-yellow-900 px-1.5 py-0.5 rounded-sm truncate"
          data-testid="unassigned-pill"
        >
          Unassigned
        </span>
      ) : (
        <span className="text-[11px] text-slate-800 font-bold truncate">— {assignees.slice(0, 2).join(", ")}{assignees.length > 2 ? ` +${assignees.length - 2}` : ""}</span>
      )}
      {tours.length > 1 && <span className="tab-tag text-[9px] text-slate-500 shrink-0 ml-auto">×{tours.length}</span>}
      {anyDropPending && <span className="tab-tag text-[9px] text-amber-700 shrink-0">DROP</span>}
      {draftHere && <span className="tab-tag text-[9px] text-amber-700 shrink-0">DRAFT</span>}
    </button>
  );
}

// Each tour renders as its own block on the schedule. We keep this as a
// "group" structure for ShiftBlock compatibility but never merge multiple
// tours together, so three Crew 1 slots on the same day at the same time
// appear as three separate rows rather than a single "×3" entry. The
// PositionGrid still groups blocks by (start, end) under a shared time
// banner — but each individual tour stays visible underneath it.
function groupTours(tours) {
  const ROLE_ORDER = ["MVO", "Head Tech", "Crew 1", "Crew 2"];
  return tours
    .map((t) => ({
      key: t.id,
      position: t.position || t.title || "—",
      start_time: t.start_time,
      end_time: t.end_time,
      required_certification: t.required_certification,
      ambulance_role: t.ambulance_role || null,
      tours: [t],
    }))
    .sort((a, b) => {
      if (a.start_time !== b.start_time) return a.start_time.localeCompare(b.start_time);
      const ar = ROLE_ORDER.indexOf(a.ambulance_role || "");
      const br = ROLE_ORDER.indexOf(b.ambulance_role || "");
      if (ar !== br) return (ar === -1 ? 99 : ar) - (br === -1 ? 99 : br);
      // Same role / time — filled slots sit above open ones (display only).
      const af = a.tours[0]?.assigned_user_id ? 0 : 1;
      const bf = b.tours[0]?.assigned_user_id ? 0 : 1;
      if (af !== bf) return af - bf;
      return (a.key || "").localeCompare(b.key || "");
    });
}

// ===== Position Grid =====
// Day-major layout. Each day is a column. Within each column, only positions
// that have at least one tour anywhere in the visible week are rendered, with
// the position name as a sub-header above its time buckets. Empty days inside
// a visible position render an em-dash.

// Clickable time bar for a single (start_time, end_time) bucket. Supervisors
// and admins see a Popover with Edit / Delete when they click the bar. Everyone
// else sees a plain non-interactive bar.
function TimeBarWithMenu({ bucket, canManage, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const label = `${fmtTime24(bucket.start_time)}–${fmtTime24(bucket.end_time)}`;
  const slotCount = bucket.groups.reduce((sum, g) => sum + ((g.tours || []).length || 1), 0);
  if (!canManage) {
    return (
      <div className="bg-slate-900 text-white font-bold mono text-[11px] tracking-tight text-left py-1 px-2 uppercase">
        {label}
      </div>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`time-bar-${bucket.time_key}`}
          className="w-full bg-slate-900 text-white font-bold mono text-[11px] tracking-tight text-left py-1 px-2 uppercase cursor-pointer hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1" data-testid={`time-bar-menu-${bucket.time_key}`}>
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 mb-1">
          {label} · {slotCount} slot{slotCount === 1 ? "" : "s"}
        </div>
        <button
          type="button"
          data-testid={`time-bar-edit-${bucket.time_key}`}
          onClick={() => { setOpen(false); onEdit && onEdit(); }}
          className="w-full text-left flex items-center gap-2 px-2 py-2 text-sm rounded-sm hover:bg-slate-100"
        >
          <PencilSimple size={14} weight="bold" /> Edit tour
        </button>
        <button
          type="button"
          data-testid={`time-bar-delete-${bucket.time_key}`}
          onClick={() => { setOpen(false); onDelete && onDelete(); }}
          className="w-full text-left flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-red-600 hover:bg-red-50"
        >
          <Trash size={14} weight="bold" /> Delete tour
        </button>
      </PopoverContent>
    </Popover>
  );
}

function PositionGrid({ positions, days, tours, currentUser, onSelectGroup, onEditBucket, onDeleteBucket }) {
  // grouped: { [position]: { [et-day-key]: [groups...] } }, plus "__other__" for unknown positions.
  // Bucketing keys are ET calendar-date strings (YYYY-MM-DD) so a 22:00-Friday
  // shift lands in the Friday column even when the reader's browser is on
  // Pacific / UTC / other timezones.
  const { grouped, visiblePositions } = useMemo(() => {
    const out = {};
    const knownPositions = new Set(positions);
    [...positions, "__other__"].forEach((p) => {
      out[p] = {};
      days.forEach((d) => { out[p][etDayKey(d.toISOString())] = []; });
    });
    const groups = groupTours(tours);
    groups.forEach((g) => {
      const p = knownPositions.has(g.position) ? g.position : "__other__";
      const k = etDayKey(g.start_time);
      if (!out[p][k]) out[p][k] = [];
      out[p][k].push(g);
    });
    // Per-position sort. Tour 1 uses a rotated key so 22:00/23:00 shifts
    // appear before 00:00/01:00/02:00 morning shifts on the same calendar day.
    Object.entries(out).forEach(([p, row]) => {
      const cmp = sortForPosition(p);
      Object.values(row).forEach((arr) => arr.sort(cmp));
    });
    // Only show positions that have at least one tour anywhere in this week.
    const visible = [...positions, "__other__"].filter((p) =>
      Object.values(out[p] || {}).some((arr) => arr.length > 0)
    );
    return { grouped: out, visiblePositions: visible };
  }, [positions, days, tours]);

  if (visiblePositions.length === 0) {
    return (
      <div className="surface-grid p-8 text-center text-slate-500" data-testid="position-grid-empty">
        <div className="font-display text-lg">No tours scheduled this week.</div>
        <div className="text-xs mt-1">Use <strong>Post Shift</strong> or <strong>Upload Template</strong> to add shifts.</div>
      </div>
    );
  }

  return (
    <div className="surface-grid overflow-x-auto" data-testid="position-grid">
      <div className="grid grid-cols-7 min-w-[1100px]">
        {days.map((d) => {
          const dKey = etDayKey(d.toISOString());
          const today = etTodayYMD() === dKey;
          const dayNum = parseInt(dKey.split("-")[2], 10);
          return (
            <div key={dKey} className="border-r border-slate-200 last:border-r-0">
              {/* Day header */}
              <div className={`text-white text-center py-2 ${today ? "bg-blue-700" : "bg-slate-900"}`}>
                <div className="tab-tag text-slate-300">{fmtDay(d)}</div>
                <div className="font-display font-bold text-xl leading-none mt-1">{dayNum}</div>
                <div className="text-[10px] text-slate-300 mono">{d.toLocaleDateString(undefined, { month: "short", timeZone: "UTC" })}</div>
              </div>

              {/* Position sections within this day */}
              <div className="divide-y divide-slate-200">
                {visiblePositions.map((p) => {
                  const cell = (grouped[p] && grouped[p][dKey]) || [];
                  // Sub-group blocks by start/end time so we render the time once
                  // as a banner, then list each role beneath.
                  const byTime = [];
                  const seen = new Map();
                  for (const g of cell) {
                    const tkey = `${g.start_time}__${g.end_time}${isSpecialEventPos(p) ? `__${(g.tours[0]?.description || "").trim()}` : ""}`;
                    if (!seen.has(tkey)) {
                      const bucket = { time_key: tkey, start_time: g.start_time, end_time: g.end_time, groups: [] };
                      seen.set(tkey, bucket);
                      byTime.push(bucket);
                    }
                    seen.get(tkey).groups.push(g);
                  }
                  return (
                    <div key={p} className="bg-white" data-testid={`day-${dKey}-pos-${p}`}>
                      <div className="px-2 py-1.5 bg-slate-300 border-b border-slate-400">
                        <div className="font-display font-bold text-black text-xs tracking-tight leading-tight uppercase text-center">{p === "__other__" ? "OTHER" : p}</div>
                      </div>
                      <div className="p-1.5">
                        {byTime.length === 0 ? (
                          <div className="h-8 flex items-center justify-center text-[10px] text-slate-300 tab-tag">—</div>
                        ) : (
                          <div className="space-y-2">
                            {byTime.map((bucket) => {
                              const banner = previousDayBanner(p, bucket.start_time);
                              // Gather unique, non-empty descriptions across every tour
                              // in this time bucket. Multiple posted shifts sharing a
                              // slot may have different callouts (e.g. "Marathon
                              // standby" vs "Trainee ride-along"); we show each one.
                              const descriptions = Array.from(new Set(
                                bucket.groups
                                  .flatMap((g) => (g.tours || []).map((t) => (t.description || "").trim()))
                                  .filter(Boolean)
                              ));
                              return (
                                <div key={bucket.time_key} data-testid={`time-bucket-${bucket.time_key}`}>
                                  {banner && (
                                    <div
                                      className="bg-yellow-300 text-yellow-950 font-black mono text-[10px] tracking-widest text-center py-1 px-1 uppercase border-t border-yellow-500"
                                      data-testid={`prev-day-banner-${bucket.time_key}`}
                                    >
                                      ⚠ {banner}
                                    </div>
                                  )}
                                  {descriptions.length > 0 && (
                                    <div
                                      className="bg-emerald-500 text-white font-bold text-[11px] leading-tight tracking-tight text-left py-1 px-2 border-b border-emerald-700"
                                      data-testid={`shift-description-${bucket.time_key}`}
                                    >
                                      {descriptions.map((d, i) => (
                                        <div key={i} className="whitespace-pre-wrap break-words">{d}</div>
                                      ))}
                                    </div>
                                  )}
                                  <TimeBarWithMenu
                                    bucket={bucket}
                                    canManage={isSupervisorOrAbove(currentUser)}
                                    onEdit={() => onEditBucket && onEditBucket(bucket)}
                                    onDelete={() => onDeleteBucket && onDeleteBucket(bucket)}
                                  />
                                  <div>
                                    {bucket.groups.map((g) => <ShiftBlock key={g.key} group={g} currentUser={currentUser} onClick={() => onSelectGroup(g)} />)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Open Shifts Board =====
function OpenShiftsBoard({ tours, currentUser, onSelect, roleEligibility }) {
  const nowMs = Date.now();
  const open = tours.filter((t) => !t.assigned_user_id && t.status === "open" && new Date(t.start_time).getTime() > nowMs).sort((a, b) => a.start_time.localeCompare(b.start_time));

  // Group same-day + same-start + same-end shifts so they render as one block
  // listing each position underneath (per request 'Group Duplicate Time Slots').
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of open) {
      const date = isoDatePart(new Date(t.start_time));
      const key = `${date}__${fmtTime24(t.start_time)}__${fmtTime24(t.end_time)}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          date_label: new Date(t.start_time).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }),
          start_time: t.start_time,
          end_time: t.end_time,
          start_hm: fmtTime24(t.start_time),
          end_hm: fmtTime24(t.end_time),
          tours: [],
        });
      }
      map.get(key).tours.push(t);
    }
    return Array.from(map.values());
  }, [open]);

  function GroupCard({ g }) {
    const hours = tourHours(g.tours[0]).toFixed(1);
    const eligibleCount = g.tours.filter((t) => userCanPickupShift(currentUser, t, roleEligibility)).length;
    return (
      <div className="surface-grid p-4 space-y-3" data-testid={`open-group-${g.key}`}>
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <div>
            <div className="mono text-2xl font-display font-extrabold tracking-tight text-slate-900 leading-none">
              {g.start_hm}<span className="text-slate-400 mx-1">–</span>{g.end_hm}
            </div>
            <div className="text-xs text-slate-500 mt-1">{g.date_label} · {hours} hrs</div>
          </div>
          <div className="text-right">
            <div className="tab-tag text-slate-500">Open Slots</div>
            <div className="mono text-2xl font-bold text-emerald-700">{g.tours.length}</div>
            {eligibleCount > 0 && eligibleCount < g.tours.length && <div className="text-[10px] text-emerald-700">({eligibleCount} eligible for you)</div>}
          </div>
        </div>
        <div className="space-y-1">
          {g.tours.map((t) => {
            const isLocked = !userCanPickupShift(currentUser, t, roleEligibility);
            return (
              <button key={t.id} type="button" onClick={() => !isLocked && onSelect(t)} disabled={isLocked}
                className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 border border-slate-200 rounded-sm transition-colors text-sm ${isLocked ? "bg-slate-50 opacity-60 cursor-not-allowed" : "bg-white hover:bg-emerald-50 hover:border-emerald-400"}`}
                data-testid={`open-shift-${t.id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {isLocked ? <Lock size={14} className="text-slate-500 shrink-0" /> : <CheckCircle size={14} weight="bold" className="text-emerald-600 shrink-0" />}
                  <span className="font-medium truncate">{t.position || t.title || "Shift"}</span>
                  {t.ambulance_role && <Badge className={`rounded-sm text-[10px] ${ROLE_COLOR[t.ambulance_role] || "bg-slate-700 text-white"}`}>{t.ambulance_role}</Badge>}
                </div>
                <Badge className={`rounded-sm text-[10px] ${CERT_COLOR[t.required_certification] || "bg-slate-700 text-white"} shrink-0`}>{t.required_certification}</Badge>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold">Open Shifts grouped by time · {groups.length} time block{groups.length === 1 ? "" : "s"} · {open.length} slot{open.length === 1 ? "" : "s"}</h3>
        <div className="tab-tag text-slate-500">Your authorized certs: <span className="text-slate-900">{(currentUser?.certifications || []).join(", ") || "—"}</span></div>
      </div>
      {groups.length === 0 ? (
        <div className="surface-grid p-6 text-center text-sm text-slate-500">No open shifts in this period.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{groups.map((g) => <GroupCard key={g.key} g={g} />)}</div>
      )}
    </div>
  );
}

// ===== My Schedule =====
function MyScheduleList({ tours, currentUser, onSelect }) {
  const now = Date.now();
  const mine = tours.filter((t) => t.assigned_user_id === currentUser?.id).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const upcoming = mine.filter((t) => new Date(t.end_time).getTime() >= now);
  const past = mine.filter((t) => new Date(t.end_time).getTime() < now);

  // Group same-date+same-time entries together to compress the list view.
  function groupByTime(rows) {
    const map = new Map();
    for (const t of rows) {
      const date = isoDatePart(new Date(t.start_time));
      const key = `${date}__${fmtTime24(t.start_time)}__${fmtTime24(t.end_time)}`;
      if (!map.has(key)) {
        map.set(key, { key, date, start_time: t.start_time, end_time: t.end_time, tours: [] });
      }
      map.get(key).tours.push(t);
    }
    return Array.from(map.values());
  }

  function GroupedTable({ rows, title, isPast }) {
    const groups = groupByTime(rows);
    return (
      <div className="surface-grid mb-6">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="font-display font-bold">{title}</div>
          <div className="mono text-xs text-slate-500">{rows.length}</div>
        </div>
        {groups.length === 0 ? (
          <div className="px-4 py-6 text-slate-500 text-sm text-center">None.</div>
        ) : groups.map((g) => (
          <div key={g.key} className="border-b border-slate-100 last:border-b-0" data-testid={`mygroup-${g.key}`}>
            <div className="px-4 py-2 bg-white flex items-center gap-3 border-b border-slate-100">
              <div className="mono font-bold text-slate-900">{new Date(g.start_time).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
              <div className="mono text-sm text-slate-700">{fmtTime24(g.start_time)}–{fmtTime24(g.end_time)}</div>
              <span className="ml-auto tab-tag text-slate-500">{g.tours.length} shift{g.tours.length === 1 ? "" : "s"}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {g.tours.map((t) => {
                  const drop = t.status === "drop_pending";
                  return (
                    <tr key={t.id} onClick={() => onSelect(t)} className={`border-b border-slate-50 last:border-b-0 cursor-pointer ${drop ? "bg-amber-50" : "hover:bg-slate-50"}`} data-testid={`myrow-${t.id}`}>
                      <td className="px-4 py-2 font-medium pl-8">{t.position || t.title || "Shift"}</td>
                      <td className="px-4 py-2">{t.ambulance_role && <Badge className={`rounded-sm text-[10px] ${ROLE_COLOR[t.ambulance_role] || "bg-slate-700 text-white"}`}>{t.ambulance_role}</Badge>}</td>
                      <td className="px-4 py-2"><Badge className={`rounded-sm ${CERT_COLOR[t.required_certification] || "bg-slate-700 text-white"}`}>{t.required_certification}</Badge></td>
                      <td className="px-4 py-2 mono text-xs">{tourHours(t).toFixed(1)}h</td>
                      <td className="px-4 py-2 tab-tag text-right pr-4">{drop ? <span className="text-amber-700">DROP PENDING</span> : isPast ? "COMPLETED" : "SCHEDULED"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }
  return <><GroupedTable rows={upcoming} title="My Upcoming Shifts" isPast={false} /><GroupedTable rows={past} title="My Recent Shifts" isPast={true} /></>;
}

// Special Event crew builder: event name + seat counts (seats use the regular ambulance eligibility rules).
function EventCrewPicker({ crew, setCrew, eventName, setEventName }) {
  return (
    <div className="space-y-2 border border-emerald-200 bg-emerald-50/50 rounded-sm p-3" data-testid="event-crew-picker">
      <div className="space-y-1.5">
        <Label className="tab-tag">Event name</Label>
        <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Marathon standby — Atlantic Ave" className="rounded-sm" data-testid="event-name" />
      </div>
      <Label className="tab-tag">Event crew positions</Label>
      <div className="grid grid-cols-5 gap-2">
        {EVENT_CREW_SEATS.map(([role, label]) => (
          <div key={label} className="space-y-1">
            <div className="text-[11px] font-semibold text-slate-700">{label}</div>
            <Input type="number" min={0} max={20} value={crew[role]} onChange={(e) => setCrew((c) => ({ ...c, [role]: e.target.value }))} className="rounded-sm mono h-9" data-testid={`event-crew-${label.replace(/\s+/g, "-").toLowerCase()}`} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-600">Same eligibility as the regular ambulance schedule: MVO = driver clearance; EMT = Head Tech (clearance C, C+, FTO); Crew 1/2 = clearance I, C, C+, FTO, or cleared driver, or Student. General staff is open to any active member. Published immediately.</p>
    </div>
  );
}

// ===== Post Shift form =====
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // 0..6

function PostShiftDialog({ open, onOpenChange, positions, certCatalog, users, roleEligibility, defaultDate, onCreated }) {
  const today = new Date();
  const [position, setPosition] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  const [reqCert, setReqCert] = useState(certCatalog[0] || "EMT");
  const [ambulanceRole, setAmbulanceRole] = useState(AMBULANCE_ROLES[0]);
  const [date, setDate] = useState(defaultDate || isoDatePart(today));
  const [startT, setStartT] = useState("08:00");
  const [endT, setEndT] = useState("16:00");
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [crew, setCrew] = useState({ MVO: 1, "Head Tech": 1, "Crew 1": 1, "Crew 2": 1, "": 0 });

  // Multi-day repeat: list of weekday indices (0=Sun..6=Sat). Empty = single-day only.
  const [repeatDays, setRepeatDays] = useState([]);

  useEffect(() => {
    if (open) {
      setPosition(positions[0] || ""); setCustomPosition("");
      setReqCert(certCatalog[0] || "EMT");
      setAmbulanceRole(AMBULANCE_ROLES[0]);
      // Default to the week currently being viewed on the Schedule
      // (the parent passes defaultDate as the Sunday of that week);
      // fall back to today when opened from a context without one.
      setDate(defaultDate || isoDatePart(today));
      setStartT("08:00"); setEndT("16:00"); setNotes(""); setDescription(""); setAssignee("");
      setRepeatDays([]);
      setQuantity("1");
      setCrew({ MVO: 1, "Head Tech": 1, "Crew 1": 1, "Crew 2": 1, "": 0 });
    }
  /* eslint-disable-next-line */
  }, [open, defaultDate]);

  const effectivePosition = position === "__custom__" ? customPosition.trim() : position;

  // Ambulance role is only required for TOUR 1/2/3. Other positions
  // (Supervisor, Communications, Logistics, Special Event, custom one-offs)
  // don't carry an ambulance role at all.
  const requiresAmbulanceRole = /^TOUR\s*[123]\b/i.test(effectivePosition || "");
  const isEvent = isSpecialEventPos(effectivePosition);

  // When ambulance role changes, prefer the lowest-tier cert eligible for that role.
  // When the position doesn't require a role, fall back to the cert catalog.
  useEffect(() => {
    if (requiresAmbulanceRole && ambulanceRole && roleEligibility?.[ambulanceRole]?.length) {
      setReqCert(roleEligibility[ambulanceRole][0]);
    } else if (!requiresAmbulanceRole) {
      setReqCert(certCatalog[0] || "EMT");
    } else {
      setReqCert(certCatalog[0] || "EMT");
    }
  /* eslint-disable-next-line */
  }, [ambulanceRole, certCatalog.length, requiresAmbulanceRole]);

  // Split active members into "eligible" and "ineligible" buckets.
  // The ineligible bucket is shown under a warning optgroup so a
  // supervisor can still intentionally pre-assign someone who doesn't
  // match (the backend will prompt for an emergency override on
  // save). This mirrors how AssignControl handles the per-slot
  // reassign dropdown — keeping the two flows consistent so names
  // never silently disappear from the roster when a supervisor
  // expected to see them.
  const activeUsers = users
    .filter((u) => u.active !== false)
    // Hard-filter restricted drivers from MVO slots. We only know a
    // single tour window at post time; use the first target date and
    // the entered start/end to run the overlap check locally.
    .filter((u) => {
      if (!requiresAmbulanceRole || ambulanceRole !== "MVO") return true;
      // Construct a probe tour matching the submit() combination logic
      // so filtering here matches what the backend would enforce.
      try {
        const probeStart = combineDateTime(date, startT);
        const probeEndDay = endT <= startT ? isoDatePart(addDays(new Date(date), 1)) : date;
        const probeEnd = combineDateTime(probeEndDay, endT);
        return !restrictionBlocksMVO(u, { ambulance_role: "MVO", start_time: probeStart, end_time: probeEnd });
      } catch { return true; }
    });
  const isMatch = (u) => {
    if (ambulanceRole) return userEligibleForRole(u, ambulanceRole, roleEligibility);
    return userIsAuthorized(u, reqCert);
  };
  const eligibleAssignees = activeUsers.filter(isMatch);
  const ineligibleAssignees = activeUsers.filter((u) => !isMatch(u));

  function toggleDay(d) {
    setRepeatDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  }
  function selectAllDays() {
    setRepeatDays(repeatDays.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6]);
  }

  // Resolve the actual list of dates the user wants to post for.
  // Single-day if no checkboxes are picked, else: the same week-of (sun..sat) as `date`.
  function targetDates() {
    if (repeatDays.length === 0) return [date];
    const base = new Date(date + "T00:00:00");
    const dow = base.getDay();
    const sunday = new Date(base);
    sunday.setDate(base.getDate() - dow);
    return repeatDays.map((d) => {
      const dt = new Date(sunday);
      dt.setDate(sunday.getDate() + d);
      return isoDatePart(dt);
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!effectivePosition) { toast.error("Pick or enter a position"); return; }
    if (requiresAmbulanceRole && !ambulanceRole) { toast.error("Pick an ambulance role"); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startT) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(endT)) { toast.error("Time must be HH:MM (24h)"); return; }
    if (isEvent) { await submitEventCrew(); return; }
    const dates = targetDates();
    // Empty / 0 / non-numeric quantity falls back to 1.
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const canPreAssign = dates.length === 1 && qty === 1;
    // If the supervisor pre-assigned an INELIGIBLE member, prompt for
    // an emergency override reason BEFORE firing any requests. Mirrors
    // the per-slot reassign flow so pre-assign behaves identically.
    let overrideReason = "";
    const pickedAssignee = canPreAssign && assignee ? users.find((u) => u.id === assignee) : null;
    if (pickedAssignee) {
      const matches = requiresAmbulanceRole && ambulanceRole
        ? userEligibleForRole(pickedAssignee, ambulanceRole, roleEligibility)
        : userIsAuthorized(pickedAssignee, reqCert);
      if (!matches) {
        const reason = window.prompt(
          `${pickedAssignee.name} is not normally eligible for this shift (${ambulanceRole || reqCert}).\n\n` +
          `Enter a reason for the emergency override, or Cancel to pick someone else.`,
        );
        if (!reason || !reason.trim()) { toast.error("Override cancelled"); return; }
        overrideReason = reason.trim();
      }
    }
    let created = 0, failed = 0;
    for (const d of dates) {
      const start = combineDateTime(d, startT);
      const endDateStr = endT <= startT ? isoDatePart(addDays(new Date(d), 1)) : d;
      const end = combineDateTime(endDateStr, endT);
      if (!start || !end || end <= start) { failed += 1; continue; }
      for (let i = 0; i < qty; i += 1) {
        try {
          await api.post("/tours/", {
            position: effectivePosition,
            title: effectivePosition,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            required_certification: reqCert,
            ambulance_role: requiresAmbulanceRole ? ambulanceRole : null,
            notes,
            description: description.trim() || null,
            assigned_user_id: canPreAssign ? (assignee || null) : null,
            emergency_override: overrideReason ? true : false,
            override_reason: overrideReason || null,
          });
          created += 1;
        } catch (err) { failed += 1; }
      }
    }
    if (created > 0) toast.success(`${created} shift${created === 1 ? "" : "s"} posted${failed ? ` (${failed} failed)` : ""} — add another or Close when done`);
    if (created === 0) { toast.error("No shifts created"); return; }
    // Keep dialog open so the user can rapidly post additional shifts. Reset
    // the lightweight per-shift inputs (notes / assignee / repeat days /
    // quantity) but preserve position + role + cert + date + times so common
    // patterns can be re-submitted with one click.
    setNotes("");
    setDescription("");
    setAssignee("");
    setRepeatDays([]);
    setQuantity("1");
    onCreated();
  }

  async function submitEventCrew() {
    if (!description.trim()) { toast.error("Enter the event name"); return; }
    const seats = EVENT_CREW_SEATS.flatMap(([role]) => Array(Math.max(0, parseInt(crew[role], 10) || 0)).fill(role));
    if (!seats.length) { toast.error("Add at least one crew position"); return; }
    const dates = targetDates();
    let created = 0, failed = 0;
    for (const d of dates) {
      const start = combineDateTime(d, startT);
      const end = combineDateTime(endT <= startT ? isoDatePart(addDays(new Date(d), 1)) : d, endT);
      for (const role of seats) {
        try {
          await api.post("/tours/", {
            position: effectivePosition, title: effectivePosition,
            start_time: start.toISOString(), end_time: end.toISOString(),
            required_certification: role ? roleLabel(role, effectivePosition) : null,
            ambulance_role: role || null, notes, description: description.trim(), published: true,
          });
          created += 1;
        } catch { failed += 1; }
      }
    }
    if (!created) { toast.error("No event shifts created"); return; }
    toast.success(`Event "${description.trim()}" posted: ${created} crew position(s)${failed ? ` (${failed} failed)` : ""}. Assign members from the schedule.`);
    setNotes(""); setDescription(""); setRepeatDays([]);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2 border-b border-slate-200">
          <DialogTitle className="font-display text-xl">Post New Shift</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-4">
          <div className="space-y-1.5">
            <Label className="tab-tag">Position / Tour Label</Label>
            <select data-testid="form-position" value={position} onChange={(e) => setPosition(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white">
              {positions.map((p) => <option key={p} value={p}>{p}</option>)}
              <option value="__custom__">+ Custom one-time position…</option>
            </select>
            {position === "__custom__" && (
              <Input data-testid="form-position-custom" placeholder="Type position name" className="rounded-sm mt-2" value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} required />
            )}
            <p className="text-[11px] text-slate-500">Manage the position list in Admin → Settings → Positions.</p>
          </div>

          {isEvent ? (
            <EventCrewPicker crew={crew} setCrew={setCrew} eventName={description} setEventName={setDescription} />
          ) : requiresAmbulanceRole ? (
            <div className="space-y-1.5">
              <Label className="tab-tag">Ambulance Role</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="form-role-grid">
                {AMBULANCE_ROLES.map((r) => (
                  <button type="button" key={r}
                    onClick={() => setAmbulanceRole(r)}
                    data-testid={`form-role-${r.replace(/\s+/g, "-")}`}
                    className={`text-xs font-semibold py-2 rounded-sm border transition-colors ${ambulanceRole === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    {r}
                  </button>
                ))}
              </div>
              {ambulanceRole && roleEligibility && (
                <p className="text-[11px] text-slate-500">
                  <strong>{ambulanceRole}</strong> eligible certs: <span className="mono">{(roleEligibility[ambulanceRole] || []).join(" · ")}</span>
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="tab-tag">Required Certification</Label>
              <select
                value={reqCert}
                onChange={(e) => setReqCert(e.target.value)}
                className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                data-testid="form-cert"
              >
                {certCatalog.map((c) => <option key={c}>{c}</option>)}
              </select>
              <p className="text-[11px] text-slate-500">No ambulance role for <strong>{effectivePosition || "this position"}</strong> — pick the cert required to fill it.</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">Date</Label>
              <Input type="date" data-testid="form-date" className="rounded-sm" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">Start (24h)</Label>
              <Input type="text" data-testid="form-start" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" placeholder="HHMM → HH:MM" maxLength={5} className="rounded-sm mono" value={startT} onChange={(e) => setStartT(normalizeTimeInput(e.target.value))} required />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">End (24h)</Label>
              <Input type="text" data-testid="form-end" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" placeholder="HHMM → HH:MM" maxLength={5} className="rounded-sm mono" value={endT} onChange={(e) => setEndT(normalizeTimeInput(e.target.value))} required />
            </div>
          </div>
          {(() => {
            const isOvernight = /^([01]\d|2[0-3]):[0-5]\d$/.test(startT) && /^([01]\d|2[0-3]):[0-5]\d$/.test(endT) && endT <= startT;
            return (
              <div className={`text-[11px] ${isOvernight ? "text-amber-700 font-medium" : "text-slate-500"}`} data-testid="overnight-hint">
                {isOvernight ? <>Overnight shift detected — end rolls to next calendar day for each posted date.</> : <>Same-day shift.</>}
              </div>
            );
          })()}

          {/* Quantity */}
          {!isEvent && <div className="space-y-1.5">
            <Label className="tab-tag">Quantity <span className="text-slate-400 normal-case font-normal">(optional · leave blank for 1)</span></Label>
            <Input
              type="number"
              min={1}
              max={50}
              inputMode="numeric"
              className="rounded-sm w-32 mono"
              placeholder="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="form-quantity"
            />
            {(() => {
              const qty = Math.max(1, parseInt(quantity, 10) || 1);
              const total = qty * Math.max(1, repeatDays.length || 1);
              if (total === 1) return <p className="text-[11px] text-slate-500">Will create 1 slot.</p>;
              return <p className="text-[11px] text-slate-500">Will create <strong>{total}</strong> slot{total === 1 ? "" : "s"}{repeatDays.length > 0 ? ` (${qty} × ${repeatDays.length} day${repeatDays.length === 1 ? "" : "s"})` : ""}.</p>;
            })()}
          </div>}

          {/* Multi-day repeat */}
          <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50/40">
            <div className="flex items-center justify-between">
              <Label className="tab-tag">Repeat across days of the week <span className="text-slate-400 normal-case font-normal">(optional)</span></Label>
              <button type="button" onClick={selectAllDays} className="text-[11px] underline text-slate-700" data-testid="form-days-all">
                {repeatDays.length === 7 ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-7 gap-2" data-testid="form-days-grid">
              {DAY_LABELS.map((lbl, i) => (
                <label key={lbl} className={`text-center text-xs font-semibold py-2 rounded-sm border cursor-pointer transition-colors ${repeatDays.includes(i) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
                  <input type="checkbox" className="hidden" checked={repeatDays.includes(i)} onChange={() => toggleDay(i)} data-testid={`form-day-${lbl}`} />
                  {lbl}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">
              {repeatDays.length === 0
                ? "No repeats — posts only on the date above."
                : <>Will post <strong>{repeatDays.length}</strong> shifts on Sun/Mon/Tue/Wed/Thu/Fri/Sat of the week containing the date above. <em>Pre-assignment is disabled for repeats.</em></>}
            </p>
          </div>

          {repeatDays.length === 0 && !isEvent && (
            <div className="space-y-1.5">
              <Label className="tab-tag">Pre-assign (optional)</Label>
              <select data-testid="form-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white">
                <option value="">— Leave open for pickup —</option>
                {eligibleAssignees.length > 0 && (
                  <optgroup label="Eligible">
                    {eligibleAssignees
                      .slice()
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/") || "no certs"}</option>)}
                  </optgroup>
                )}
                {ineligibleAssignees.length > 0 && (
                  <optgroup label="⚠ Requires Emergency Override">
                    {ineligibleAssignees
                      .slice()
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/") || "no certs"}</option>)}
                  </optgroup>
                )}
              </select>
              <p className="text-[11px] text-slate-500">
                {ambulanceRole
                  ? <>Eligible for <strong>{ambulanceRole}</strong>: <span className="mono">{(roleEligibility[ambulanceRole] || []).join(" · ")}</span>. Members without those certs appear under <em>Emergency Override</em> — picking one requires supervisor sign-off at save.</>
                  : <>Members holding <strong>{reqCert}</strong> appear under <em>Eligible</em>; everyone else is grouped under <em>Emergency Override</em>.</>}
                {eligibleAssignees.length + ineligibleAssignees.length === 0 && <span className="text-red-600"> No active members found.</span>}
              </p>
            </div>
          )}

          {!isEvent && <div className="space-y-1.5">
            <Label className="tab-tag">Description <span className="text-emerald-700 font-semibold normal-case tracking-normal">(shows in green under the tour banner)</span></Label>
            <Input
              type="text"
              className="rounded-sm"
              data-testid="form-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Marathon standby · Bring bike helmet"
              maxLength={140}
            />
          </div>}

          <div className="space-y-1.5"><Label className="tab-tag">Notes</Label><Textarea className="rounded-sm" data-testid="form-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="form-cancel">Close</Button>
            <Button type="submit" data-testid="form-submit" className="rounded-sm bg-slate-900 text-white">
              {(() => {
                if (isEvent) return "Post Event Crew";
                const qty = Math.max(1, parseInt(quantity, 10) || 1);
                const total = qty * Math.max(1, repeatDays.length || 1);
                return total > 1 ? `Post ${total} Shifts` : "Post Shift";
              })()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===== Save current week as a Template =====
function SaveTemplateDialog({ open, onOpenChange, tours, weekStart, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const [overrideId, setOverrideId] = useState("");   // which existing template to replace
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Group tours of the current week into slots. Every part of the slot
  // key — day-of-week, start_time HH:MM, end_time HH:MM — is resolved
  // against Eastern Time via `_etParts`, NOT the browser's local
  // timezone. Without this, a late-Sunday shift (Tour 1 starting 22:00
  // ET Sunday, stored as 02:00 UTC Monday) would be bucketed as Monday
  // for a reader whose browser is in UTC / Europe / Asia — which is
  // exactly the "Sunday slots keep disappearing" bug BSVAC reported.
  const slots = useMemo(() => {
    if (!open) return [];
    const map = new Map();
    tours.forEach((t) => {
      const sp = _etParts(t.start_time);
      const ep = _etParts(t.end_time);
      if (!sp || !ep) return;
      const startHM = `${sp.hour}:${sp.minute}`;
      const endHM = `${ep.hour}:${ep.minute}`;
      const dow = sp.dowNum;
      const key = `${dow}_${startHM}_${endHM}_${t.position || t.title}_${t.required_certification}_${t.ambulance_role || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          day_of_week: dow,
          start_time: startHM,
          end_time: endHM,
          position: t.position || t.title || "",
          required_certification: t.required_certification,
          ambulance_role: t.ambulance_role || null,
          count: 0,
        });
      }
      map.get(key).count += 1;
    });
    return Array.from(map.values());
  }, [open, tours]);

  useEffect(() => {
    if (open) {
      setName(""); setDescription(""); setOverrideId(""); setConfirmOpen(false);
      api.get("/templates/").then((r) => setExisting(r.data || [])).catch(() => setExisting([]));
    }
  }, [open]);

  async function doSave() {
    setBusy(true);
    try {
      if (overrideId) {
        // Replace payload — keep original template id, use override
        // template's existing name unless the operator typed a new one.
        const target = existing.find((e) => e.id === overrideId);
        await api.patch(`/templates/${overrideId}`, {
          name: name.trim() || target?.name,
          description: description.trim() || target?.description || null,
          slots,
        });
        toast.success(`Template "${target?.name}" replaced (${slots.length} slot${slots.length === 1 ? "" : "s"})`);
      } else {
        if (!name.trim()) { toast.error("Name required"); setBusy(false); return; }
        await api.post("/templates/", { name: name.trim(), description: description.trim() || null, slots });
        toast.success(`Template saved (${slots.length} slot${slots.length === 1 ? "" : "s"})`);
      }
      setConfirmOpen(false);
      onOpenChange(false); onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  function askSave() {
    if (slots.length === 0) { toast.error("This week has no shifts to save"); return; }
    if (overrideId) {
      // Show the required confirmation before replacing.
      setConfirmOpen(true);
      return;
    }
    if (!name.trim()) { toast.error("Name required — or pick a template to override"); return; }
    doSave();
  }

  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const overrideTarget = existing.find((e) => e.id === overrideId);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="save-tpl-dialog">
          <DialogHeader><DialogTitle>Save current week as Template</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">
            Week of {weekStart && weekStart.toLocaleDateString()}. The {tours.length} shift{tours.length === 1 ? "" : "s"} below will be condensed into <strong>{slots.length}</strong> reusable slot{slots.length === 1 ? "" : "s"} (identical shifts merge into a count).
          </p>
          {existing.length > 0 && (
            <div className="border border-slate-200 rounded-sm p-2 bg-slate-50 space-y-1.5" data-testid="save-tpl-existing">
              <div className="flex items-center justify-between">
                <div className="tab-tag text-slate-500">Existing Templates</div>
                <div className="text-[10px] text-slate-500">Pick one to override, or save as new below</div>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {existing.map((t) => (
                  <label
                    key={t.id}
                    className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm cursor-pointer border ${overrideId === t.id ? "bg-amber-50 border-amber-400" : "bg-white border-slate-200 hover:border-slate-400"}`}
                    data-testid={`save-tpl-existing-${t.id}`}
                  >
                    <input
                      type="radio"
                      name="tpl-override"
                      checked={overrideId === t.id}
                      onChange={() => setOverrideId(overrideId === t.id ? "" : t.id)}
                    />
                    <span className="font-medium">{t.name}</span>
                    <span className="text-[10px] text-slate-500 ml-auto">{t.slots?.length ?? 0} slot(s)</span>
                  </label>
                ))}
                {overrideId && (
                  <button
                    type="button"
                    onClick={() => setOverrideId("")}
                    className="text-[10px] text-slate-500 underline"
                    data-testid="save-tpl-override-clear"
                  >
                    Clear override — save as brand-new template
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="space-y-2 pt-2">
            <div>
              <Label className="tab-tag">Name {overrideId && <span className="text-[10px] text-slate-500 normal-case">(leave blank to keep "{overrideTarget?.name}")</span>}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" placeholder={overrideId ? overrideTarget?.name : ""} data-testid="save-tpl-name" />
            </div>
            <div><Label className="tab-tag">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm" data-testid="save-tpl-desc" /></div>
            <div className="border border-slate-200 rounded-sm max-h-52 overflow-y-auto bg-slate-50 p-2">
              {slots.length === 0 ? <div className="text-sm text-slate-500 text-center py-3">No shifts in this week to save.</div> :
                slots.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border border-slate-200 bg-white rounded-sm px-2 py-1.5 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="rounded-sm bg-slate-900 text-white text-[10px]">{dow[s.day_of_week]}</Badge>
                      <span className="mono">{s.start_time}–{s.end_time}</span>
                      <span className="font-medium">{s.position}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {s.ambulance_role && <Badge className={`rounded-sm text-[10px] ${ROLE_COLOR[s.ambulance_role] || "bg-slate-700 text-white"}`}>{s.ambulance_role}</Badge>}
                      <Badge className="rounded-sm bg-blue-600 text-white text-[10px]">{s.required_certification}</Badge>
                      <span className="tab-tag">×{s.count}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={askSave} disabled={busy || slots.length === 0} className={`rounded-sm text-white ${overrideId ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-900"}`} data-testid="save-tpl-go">
              <FloppyDisk size={14} className="mr-1" />
              {busy ? "Saving…" : (overrideId ? "Replace Selected" : "Save Template")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal — required by spec before replacing an existing template */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="save-tpl-confirm">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2"><Warning size={16} weight="fill" /> Replace Template?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-700">
            <p>You're about to replace <strong>"{overrideTarget?.name}"</strong> with the current week's {slots.length} slot(s).</p>
            <p className="text-[11px] text-slate-500">The old slot configuration will be discarded. Any weeks already loaded from the previous version are unaffected.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={doSave} disabled={busy} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-tpl-confirm-go">
              <FloppyDisk size={14} className="mr-1" />{busy ? "Replacing…" : "Yes, Replace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const MAX_TPL_WEEKS = 104;

function TemplateApplyResult({ result }) {
  const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="border border-slate-200 rounded-sm p-2 text-xs max-h-56 overflow-y-auto" data-testid="upload-tpl-result">
      <div className="font-semibold mb-1" data-testid="upload-tpl-result-summary">
        {result.weeks_succeeded} of {result.weeks_requested} week(s) updated · {result.created} shift(s) created
        {result.skipped_duplicates ? ` · ${result.skipped_duplicates} already existed (skipped)` : ""}
      </div>
      {result.weeks.map((w) => (
        <div key={w.week_start} className={`flex items-center justify-between px-2 py-1 mb-1 rounded-sm border ${w.status === "ok" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid={`upload-tpl-week-${w.week_start}`}>
          <span className="mono">{fmt(w.week_start)} – {fmt(w.week_end)}</span>
          {w.status === "ok"
            ? <span className="text-emerald-700">Updated · {w.created} created{w.skipped_duplicates ? `, ${w.skipped_duplicates} skipped` : ""}</span>
            : <span className="text-red-700">Failed · {w.error}</span>}
        </div>
      ))}
    </div>
  );
}

// ===== Upload (apply) a saved Template into the current week =====
function UploadTemplateDialog({ open, onOpenChange, weekStart, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [weeks, setWeeks] = useState(1);
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/templates/").then((r) => { setTemplates(r.data); setSelectedId(r.data[0]?.id || ""); }).catch(() => {});
      setWeeks(1); setPublished(false); setResult(null);
    }
  }, [open]);

  const selected = templates.find((t) => t.id === selectedId);

  async function go() {
    if (!selected) { toast.error("Choose a template"); return; }
    const n = parseInt(weeks, 10);
    if (!Number.isInteger(n) || n < 1 || n > MAX_TPL_WEEKS) { toast.error(`Enter a number of weeks between 1 and ${MAX_TPL_WEEKS}`); return; }
    setBusy(true); setResult(null);
    try {
      const week_start = isoDatePart(weekStart);
      const { data } = await api.post(`/templates/${selected.id}/apply`, { week_start, weeks: n, published }, { timeout: 300000 });
      setResult(data);
      if (data.weeks_failed) toast.error(`${data.weeks_failed} of ${data.weeks_requested} week(s) failed — see details`);
      else toast.success(`Loaded ${data.created} shifts across ${data.weeks_succeeded} week(s)`);
      onApplied && onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || (err.code === "ECONNABORTED" ? "The server took too long to respond. Check the schedule before retrying — duplicates are skipped automatically." : "Failed to apply template"));
    }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Upload Template into the schedule</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-3">Loads the chosen template starting from the week of <strong className="mono">{weekStart && weekStart.toLocaleDateString()}</strong>.</p>
        <div className="space-y-3">
          <div>
            <Label className="tab-tag">Template</Label>
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" data-testid="upload-tpl-select">
              {templates.length === 0 ? <option value="">— No templates saved yet —</option> : templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.slots.length} slot(s)</option>)}
            </select>
          </div>
          {selected && (
            <div className="border border-slate-200 rounded-sm bg-slate-50 p-2 max-h-48 overflow-y-auto text-xs">
              {selected.slots.map((s, i) => (
                <div key={i} className="flex items-center justify-between mb-1 bg-white border border-slate-200 rounded-sm px-2 py-1">
                  <span><span className="font-semibold">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][s.day_of_week]}</span> <span className="mono">{s.start_time}–{s.end_time}</span> · {s.position}</span>
                  <span className="flex items-center gap-1">
                    {s.ambulance_role && <Badge className={`rounded-sm text-[10px] ${ROLE_COLOR[s.ambulance_role] || "bg-slate-700 text-white"}`}>{s.ambulance_role}</Badge>}
                    <Badge className="rounded-sm bg-blue-600 text-white text-[10px]">{s.required_certification}</Badge>
                    <span className="tab-tag">×{s.count}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div><Label className="tab-tag">Number of consecutive weeks</Label><Input type="number" min="1" max={MAX_TPL_WEEKS} value={weeks} onChange={(e) => setWeeks(e.target.value)} className="rounded-sm mono" data-testid="upload-tpl-weeks" /></div>
          <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm p-2 bg-slate-50">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="upload-tpl-publish" />
            <span><strong>Publish immediately</strong> — volunteers will see and be able to pick up. Leave unchecked to load as draft.</span>
          </label>
        </div>
        {result && <TemplateApplyResult result={result} />}
        <DialogFooter><Button onClick={go} disabled={busy || !selected} className="rounded-sm bg-slate-900 text-white" data-testid="upload-tpl-go">{published ? <Broadcast size={14} className="mr-1" /> : <EyeSlash size={14} className="mr-1" />}{busy ? "Loading…" : (published ? "Load & Publish" : "Load as Draft")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Bulk Clear Dialog =====
// Supervisors / admins can nuke shifts across a date range or "all forward"
// in a single click. Backend clamps the effective start to today (UTC) so a
// mispasted old date can't scrub history — the UI mirrors that safety.
function BulkClearDialog({ open, onOpenChange, onCleared }) {
  const isoToday = () => new Date().toISOString().slice(0, 10);
  // Modes: "weeks" (multi-select published weeks) | "range" (date pair) | "all_forward"
  const [mode, setMode] = useState("weeks");
  const [start, setStart] = useState(isoToday());
  const [end, setEnd] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 28);
    return d.toISOString().slice(0, 10);
  });
  const [publishedWeeks, setPublishedWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [weekQuery, setWeekQuery] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("weeks");
    setSelectedWeeks(new Set());
    setWeekQuery("");
    api.get("/tours/published-weeks")
      .then((r) => setPublishedWeeks(r.data?.weeks || []))
      .catch(() => setPublishedWeeks([]));
  }, [open]);

  const filteredWeeks = useMemo(() => {
    const needle = weekQuery.trim().toLowerCase();
    if (!needle) return publishedWeeks;
    return publishedWeeks.filter((w) => {
      const label = new Date(w.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      return `${w.week_start} ${label}`.toLowerCase().includes(needle);
    });
  }, [publishedWeeks, weekQuery]);

  function toggleWeek(iso) {
    setSelectedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  }
  function selectAll() { setSelectedWeeks(new Set(filteredWeeks.map((w) => w.week_start))); }
  function clearSel() { setSelectedWeeks(new Set()); }

  function askConfirm() {
    if (mode === "weeks") {
      if (selectedWeeks.size === 0) { toast.error("Pick at least one week to clear."); return; }
    }
    if (mode === "range" && (!start || !end)) { toast.error("Pick a start and end date"); return; }
    if (mode === "range" && new Date(end) < new Date(start)) { toast.error("End date must be on/after start date"); return; }
    setConfirmOpen(true);
  }

  async function go() {
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "weeks") {
        // Clear each selected week: 00:00 ET Sunday → 00:00 ET next Sunday (exclusive).
        let total = 0;
        for (const wk of selectedWeeks) {
          const nextWk = addDays(ymdToUtcNoon(wk), 7).toISOString().slice(0, 10);
          const { data } = await api.post("/tours/bulk-delete", {
            start: combineDateTime(wk, "00:00").toISOString(), end: combineDateTime(nextWk, "00:00").toISOString(),
          });
          total += data.deleted || 0;
        }
        toast.success(`${total} shift${total === 1 ? "" : "s"} cleared across ${selectedWeeks.size} week(s)`);
      } else {
        const { data } = await api.post("/tours/bulk-clear", {
          mode,
          start_date: mode === "range" ? start : undefined,
          end_date: mode === "range" ? end : undefined,
        });
        if (data.deleted === 0) {
          toast.info(data.note || "No shifts matched — nothing cleared");
        } else {
          toast.success(`${data.deleted} shift${data.deleted === 1 ? "" : "s"} cleared`);
        }
      }
      setConfirmOpen(false);
      onCleared && onCleared();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk clear failed");
    } finally {
      setBusy(false);
    }
  }

  const selectedList = Array.from(selectedWeeks).sort();
  const fmtWeek = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="bulk-clear-dialog">
          <DialogHeader><DialogTitle>Clear Schedule</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Past shifts are always preserved for hours & audit — this action only clears TODAY forward.
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setMode("weeks")}
                className={`rounded-sm border px-3 py-2 text-sm text-left ${mode === "weeks" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
                data-testid="bulk-clear-mode-weeks"
              >
                <div className="font-semibold">Published Weeks</div>
                <div className="text-[11px] opacity-80">Pick specific weeks</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("range")}
                className={`rounded-sm border px-3 py-2 text-sm text-left ${mode === "range" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
                data-testid="bulk-clear-mode-range"
              >
                <div className="font-semibold">Date Range</div>
                <div className="text-[11px] opacity-80">Custom span</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("all_forward")}
                className={`rounded-sm border px-3 py-2 text-sm text-left ${mode === "all_forward" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200"}`}
                data-testid="bulk-clear-mode-all"
              >
                <div className="font-semibold">All Forward</div>
                <div className="text-[11px] opacity-80">Tomorrow onward</div>
              </button>
            </div>

            {mode === "weeks" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search weeks (e.g. Oct 12, 2026-11)…"
                    value={weekQuery}
                    onChange={(e) => setWeekQuery(e.target.value)}
                    className="rounded-sm flex-1"
                    data-testid="bulk-clear-week-search"
                  />
                  <Button type="button" size="sm" variant="outline" className="rounded-sm h-9 text-xs" onClick={selectAll} disabled={filteredWeeks.length === 0} data-testid="bulk-clear-select-all">Select All</Button>
                  <Button type="button" size="sm" variant="ghost" className="rounded-sm h-9 text-xs" onClick={clearSel} disabled={selectedWeeks.size === 0} data-testid="bulk-clear-clear-sel">Clear</Button>
                </div>
                <div className="border border-slate-200 rounded-sm max-h-72 overflow-y-auto bg-white">
                  {filteredWeeks.length === 0 ? (
                    <div className="text-xs text-slate-500 text-center p-4 italic">No published weeks match this search.</div>
                  ) : filteredWeeks.map((w) => {
                    const checked = selectedWeeks.has(w.week_start);
                    return (
                      <label
                        key={w.week_start}
                        className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-sm cursor-pointer ${checked ? "bg-red-50" : "hover:bg-slate-50"}`}
                        data-testid={`bulk-clear-week-${w.week_start}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleWeek(w.week_start)} />
                        <span className="font-medium">Week of {fmtWeek(w.week_start)}</span>
                        <span className="ml-auto text-[10px] mono text-slate-500">
                          {w.count} shifts · {w.assigned_count} assigned / {w.unassigned_count} open
                        </span>
                      </label>
                    );
                  })}
                </div>
                {selectedWeeks.size > 0 && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5" data-testid="bulk-clear-selected-count">
                    <strong>{selectedWeeks.size}</strong> week(s) selected for deletion
                  </div>
                )}
              </div>
            )}

            {mode === "range" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="tab-tag">Start date</Label>
                  <Input type="date" value={start} min={isoToday()} onChange={(e) => setStart(e.target.value)} className="rounded-sm" data-testid="bulk-clear-start" />
                </div>
                <div>
                  <Label className="tab-tag">End date</Label>
                  <Input type="date" value={end} min={start || isoToday()} onChange={(e) => setEnd(e.target.value)} className="rounded-sm" data-testid="bulk-clear-end" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={askConfirm} disabled={busy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-clear-go">
              <Trash size={14} weight="bold" className="mr-1" />Review & Clear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation — always shows exactly what will be deleted */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="bulk-clear-confirm">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><Warning size={16} weight="fill" /> Confirm Deletion</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            {mode === "weeks" && (
              <>
                <p>The following <strong>{selectedList.length}</strong> published week(s) will be deleted:</p>
                <ul className="border border-slate-200 rounded-sm max-h-48 overflow-y-auto text-xs mono">
                  {selectedList.map((iso) => <li key={iso} className="px-2 py-1 border-b border-slate-100" data-testid={`bulk-clear-confirm-${iso}`}>Week of {fmtWeek(iso)}</li>)}
                </ul>
              </>
            )}
            {mode === "range" && <p>All shifts from <strong className="mono">{start}</strong> through <strong className="mono">{end}</strong> will be deleted.</p>}
            {mode === "all_forward" && <p>Every forward-facing shift (tomorrow onward) will be deleted.</p>}
            <p className="text-[11px] text-slate-500">This cannot be undone. Past shifts are always preserved.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={go} disabled={busy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-clear-confirm-go">
              <Trash size={14} weight="bold" className="mr-1" />{busy ? "Clearing…" : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ===== Edit Shift Dialog =====
// Supervisors / admins use this to edit a previously-posted tour. Backend PATCH
// validates cert + role eligibility against the currently assigned member.
function EditShiftDialog({ tour, open, onOpenChange, positions, certCatalog, roleEligibility, onSaved }) {
  const [position, setPosition] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  const [reqCert, setReqCert] = useState("EMT");
  const [ambulanceRole, setAmbulanceRole] = useState(AMBULANCE_ROLES[0]);
  const [date, setDate] = useState("");
  const [startT, setStartT] = useState("");
  const [endT, setEndT] = useState("");
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !tour) return;
    const pos = tour.position || tour.title || "";
    setPosition(positions.includes(pos) ? pos : "__custom__");
    setCustomPosition(positions.includes(pos) ? "" : pos);
    setReqCert(tour.required_certification || certCatalog[0] || "EMT");
    setAmbulanceRole(tour.ambulance_role || (isSpecialEventPos(pos) ? "" : AMBULANCE_ROLES[0]));
    // Dates/times are edited on the ET clock (same as Post Shift / the grid).
    setDate(etDayKey(tour.start_time));
    setStartT(fmtTime24(tour.start_time));
    setEndT(fmtTime24(tour.end_time));
    setNotes(tour.notes || "");
    setDescription(tour.description || "");
  }, [open, tour, positions, certCatalog]);

  if (!tour) return null;
  const effectivePosition = position === "__custom__" ? customPosition.trim() : position;

  // Ambulance role is only required for TOUR 1/2/3 (must match Post Shift logic).
  const requiresAmbulanceRole = /^TOUR\s*[123]\b/i.test(effectivePosition || "");
  const isEvent = isSpecialEventPos(effectivePosition);
  // Bulk mode: caller stashed every tour id in the clicked bucket on the tour
  // object. In this mode we hide per-slot fields (role/cert/assignee) and
  // PATCH shared fields (time/position/description/notes) across every tour.
  const bulkIds = Array.isArray(tour.__bucketTourIds) ? tour.__bucketTourIds : null;
  const isBulk = !!bulkIds && bulkIds.length > 1;

  async function save(e) {
    e.preventDefault();
    if (!effectivePosition) { toast.error("Pick or enter a position"); return; }
    if (!isBulk && requiresAmbulanceRole && !ambulanceRole) { toast.error("Pick an ambulance role"); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startT) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(endT)) { toast.error("Time must be HH:MM"); return; }
    const start = combineDateTime(date, startT);
    const endDateStr = endT <= startT ? isoDatePart(addDays(new Date(date), 1)) : date;
    const end = combineDateTime(endDateStr, endT);
    if (!start || !end || end <= start) { toast.error("Invalid time range"); return; }
    setBusy(true);
    try {
      if (isBulk) {
        // Update every tour in the bucket with the shared fields only.
        const payload = {
          position: effectivePosition,
          title: effectivePosition,
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          notes,
          description: description.trim() || null,
        };
        let ok = 0, fail = 0;
        for (const tid of bulkIds) {
          try { await api.patch(`/tours/${tid}`, payload); ok += 1; } catch { fail += 1; }
        }
        toast.success(`Tour updated — ${ok} slot${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
      } else {
        await api.patch(`/tours/${tour.id}`, {
          position: effectivePosition,
          title: effectivePosition,
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          required_certification: reqCert,
          ambulance_role: requiresAmbulanceRole ? ambulanceRole : (isEvent ? (ambulanceRole || "") : null),
          notes,
          description: description.trim() || null,
        });
        toast.success("Shift updated");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="pb-2 border-b border-slate-200">
          <DialogTitle className="font-display text-xl">{isBulk ? `Edit Tour · ${bulkIds.length} slots` : "Edit Shift"}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-4">
          <div className="space-y-1.5">
            <Label className="tab-tag">Position / Tour Label</Label>
            <select value={position} onChange={(e) => setPosition(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" data-testid="edit-position">
              {positions.map((p) => <option key={p} value={p}>{p}</option>)}
              <option value="__custom__">+ Custom one-time position…</option>
            </select>
            {position === "__custom__" && (
              <Input className="rounded-sm mt-2" value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} required data-testid="edit-position-custom" />
            )}
          </div>

          {isBulk && (
            <div className="text-[12px] bg-emerald-50 border border-emerald-300 rounded-sm p-2 text-emerald-900" data-testid="edit-bulk-note">
              Editing the <strong>entire tour</strong> ({bulkIds.length} slots at this time). Changes to date, time, position, description and notes apply to every slot. Per-slot fields (role, cert, assignment) are unchanged — edit an individual role block on the schedule for those.
            </div>
          )}

          {!isBulk && isEvent && (
            <div className="space-y-1.5">
              <Label className="tab-tag">Event crew position</Label>
              <div className="grid grid-cols-5 gap-2" data-testid="edit-event-role-grid">
                {EVENT_CREW_SEATS.map(([r, label]) => (
                  <button type="button" key={label} onClick={() => setAmbulanceRole(r)} data-testid={`edit-event-role-${label.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-xs font-semibold py-2 rounded-sm border transition-colors ${(ambulanceRole || "") === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!isBulk && requiresAmbulanceRole && (
            <div className="space-y-1.5">
              <Label className="tab-tag">Ambulance Role</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="edit-role-grid">
                {AMBULANCE_ROLES.map((r) => (
                  <button type="button" key={r} onClick={() => setAmbulanceRole(r)} data-testid={`edit-role-${r.replace(/\s+/g, "-")}`}
                    className={`text-xs font-semibold py-2 rounded-sm border transition-colors ${ambulanceRole === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    {r}
                  </button>
                ))}
              </div>
              {ambulanceRole && roleEligibility && (
                <p className="text-[11px] text-slate-500">
                  <strong>{ambulanceRole}</strong> eligible certs: <span className="mono">{(roleEligibility[ambulanceRole] || []).join(" · ")}</span>
                </p>
              )}
            </div>
          )}

          {!isBulk && (
            <div className="space-y-1.5">
              <Label className="tab-tag">Required Certification</Label>
              <select value={reqCert} onChange={(e) => setReqCert(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" data-testid="edit-cert">
                {certCatalog.map((c) => <option key={c}>{c}</option>)}
              </select>
              {!requiresAmbulanceRole && <p className="text-[11px] text-slate-500">No ambulance role for <strong>{effectivePosition || "this position"}</strong>.</p>}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">Date</Label>
              <Input type="date" className="rounded-sm" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-date" required />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">Start (24h)</Label>
              <Input type="text" className="rounded-sm mono" value={startT} onChange={(e) => setStartT(normalizeTimeInput(e.target.value))} placeholder="HHMM → HH:MM" maxLength={5} data-testid="edit-start" required />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">End (24h)</Label>
              <Input type="text" className="rounded-sm mono" value={endT} onChange={(e) => setEndT(normalizeTimeInput(e.target.value))} placeholder="HHMM → HH:MM" maxLength={5} data-testid="edit-end" required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="tab-tag">Description <span className="text-emerald-700 font-semibold normal-case tracking-normal">(shows in green under the tour banner)</span></Label>
            <Input
              type="text"
              className="rounded-sm"
              data-testid="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Marathon standby · Bring bike helmet"
              maxLength={140}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="tab-tag">Notes</Label>
            <Textarea className="rounded-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" />
          </div>

          {tour.assigned_user_name && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              This shift is currently assigned to <strong>{tour.assigned_user_name}</strong>. Changing the cert or role will be rejected if they're no longer eligible — unassign first if needed.
            </p>
          )}

          <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="edit-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 text-white" data-testid="edit-save">{busy ? "Saving…" : "Save Changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===== Supervisor Assign Control =====
function AssignControl({ tour, users, roleEligibility, onAssign, onUnassign, idx }) {
  const [pick, setPick] = useState("");
  const [q, setQ] = useState("");
  // Split members: eligible for this position vs everyone else. The
  // list shows both so supervisors can consciously pick an ineligible
  // member — the backend + assignTour() will then prompt for an
  // emergency override before completing the assignment.
  // Mirrors backend precedence: ambulance_role eligibility is the gate
  // when a role is set, else the cert must match. Lets supervisors
  // assign Interns to Crew 1 / Crew 2 even when the shift was posted
  // with required_certification = EMT.
  const isEligible = (u) => tour.ambulance_role
    ? userEligibleForRole(u, tour.ambulance_role, roleEligibility)
    : userIsAuthorized(u, tour.required_certification);
  // Restricted MVO drivers are hard-filtered — no override path, per spec.
  const activeUsers = users
    .filter((u) => u.active !== false && u.id !== tour.assigned_user_id)
    .filter((u) => !restrictionBlocksMVO(u, tour));
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? activeUsers.filter((u) => `${u.name} ${(u.certifications || []).join(" ")}`.toLowerCase().includes(needle))
    : activeUsers;
  const eligible = filtered.filter(isEligible).sort((a, b) => a.name.localeCompare(b.name));
  const ineligible = filtered.filter((u) => !isEligible(u)).sort((a, b) => a.name.localeCompare(b.name));

  function confirm() {
    if (!pick) return;
    onAssign(pick);
    setPick("");
  }
  function cancel() { setPick(""); setQ(""); }

  if (tour.assigned_user_id) {
    return (
      <div className="border-t border-slate-200 pt-2 mt-1 space-y-2">
        <div className="tab-tag text-slate-500">Assignment</div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members…"
          className="rounded-sm h-8 text-xs"
          data-testid={`reassign-search-${idx}`}
        />
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="w-full border border-slate-200 rounded-sm h-8 px-2 text-xs bg-white" data-testid={`reassign-select-${idx}`}>
          <option value="">— Reassign to —</option>
          {eligible.length > 0 && (
            <optgroup label="Eligible">
              {eligible.map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/")}</option>)}
            </optgroup>
          )}
          {ineligible.length > 0 && (
            <optgroup label="⚠ Requires Emergency Override">
              {ineligible.map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/") || "no certs"}</option>)}
            </optgroup>
          )}
        </select>
        {pick && (
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" variant="outline" onClick={cancel} className="rounded-sm h-8 text-[11px]" data-testid={`btn-reassign-cancel-${idx}`}>Cancel</Button>
            <Button size="sm" onClick={confirm} className="rounded-sm h-8 text-[11px] bg-slate-900 text-white" data-testid={`btn-reassign-ok-${idx}`}>OK — Reassign</Button>
          </div>
        )}
        <Button size="sm" variant="outline" onClick={onUnassign} className="w-full rounded-sm h-7 text-[11px] border-red-300 text-red-600 hover:bg-red-50" data-testid={`btn-unassign-${idx}`}>Unassign</Button>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 pt-2 mt-1 space-y-2">
      <div className="tab-tag text-slate-500">Assign Member</div>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search members…"
        className="rounded-sm h-8 text-xs"
        data-testid={`assign-search-${idx}`}
      />
      <select value={pick} onChange={(e) => setPick(e.target.value)} className="w-full border border-slate-200 rounded-sm h-8 px-2 text-xs bg-white" data-testid={`assign-select-${idx}`}>
        <option value="">— Eligible for {tour.ambulance_role || tour.required_certification} —</option>
        {eligible.length > 0 && (
          <optgroup label="Eligible">
            {eligible.map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/")}</option>)}
          </optgroup>
        )}
        {ineligible.length > 0 && (
          <optgroup label="⚠ Requires Emergency Override">
            {ineligible.map((u) => <option key={u.id} value={u.id}>{u.name} · {(u.certifications || []).join("/") || "no certs"}</option>)}
          </optgroup>
        )}
      </select>
      {pick && (
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" variant="outline" onClick={cancel} className="rounded-sm h-8 text-[11px]" data-testid={`btn-assign-cancel-${idx}`}>Cancel</Button>
          <Button size="sm" onClick={confirm} className="rounded-sm h-8 text-[11px] bg-slate-900 text-white" data-testid={`btn-assign-ok-${idx}`}>OK — Assign</Button>
        </div>
      )}
      {eligible.length === 0 && <p className="text-[10px] text-red-600">No active members are authorized for this cert + role combination.</p>}
    </div>
  );
}

// ===== Main =====
export default function Schedule() {
  const { user } = useAuth();
  const isSup = isSupervisorOrAbove(user);

  const [view, setView] = useState("position");
  const [searchParams] = useSearchParams();
  const weekParam = searchParams.get("week");
  const [cursor, setCursor] = useState(() => startOfWeek(/^\d{4}-\d{2}-\d{2}$/.test(weekParam || "") ? weekParam : new Date()));
  useEffect(() => { if (/^\d{4}-\d{2}-\d{2}$/.test(weekParam || "")) setCursor(startOfWeek(weekParam)); }, [weekParam]);
  const [tours, setTours] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [roleEligibility, setRoleEligibility] = useState({});
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [tradeTour, setTradeTour] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openSave, setOpenSave] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openHealth, setOpenHealth] = useState(false);
  const health = useScheduleHealth(isSup);
  const [openBulkClear, setOpenBulkClear] = useState(false);
  const [dropReason, setDropReason] = useState("");
  const [filterPosition, setFilterPosition] = useState("ALL");
  const [filterCert, setFilterCert] = useState("ALL");
  // Emergency-override confirmation. Populated by assignTour when the
  // backend answers 409 with `requires_override: true`; the dialog is
  // dismissed either by cancellation or by re-calling assignTour with
  // emergency_override=true.
  const [overridePrompt, setOverridePrompt] = useState(null);
  // Bucket-level "Delete entire tour" confirmation. bucketToDelete holds the
  // clicked (position, start_time, end_time) bucket so we can iterate every
  // tour id inside it. bucketDeleting toggles a busy state while we PATCH.
  const [bucketToDelete, setBucketToDelete] = useState(null);
  const [bucketDeleting, setBucketDeleting] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(cursor, i)), [cursor]);
  // Week range = 00:00 ET Sunday → 00:00 ET next Sunday, using the UTC offset in
  // effect AT midnight (not at noon) so DST-change Sundays keep their 00:00 shifts.
  const range = useMemo(() => ({
    start: combineDateTime(etDayKey(days[0].toISOString()), "00:00"),
    end: combineDateTime(etDayKey(addDays(days[6], 1).toISOString()), "00:00"),
  }), [days]);
  const positions = settings?.positions || [];
  const certCatalog = settings?.certifications_catalog || ["Trainee", "Driver", "EMT", "Paramedic", "Supervisor"];

  async function load() {
    const incl = isSup ? "&include_unpublished=true" : "";
    const [t, u, s, r] = await Promise.all([
      api.get(`/tours/?start=${range.start.toISOString()}&end=${range.end.toISOString()}${incl}`),
      isSup ? api.get("/users/") : Promise.resolve({ data: [] }),
      api.get("/settings/").catch(() => ({ data: null })),
      api.get("/tours/role-eligibility").catch(() => ({ data: {} })),
    ]);
    setTours(t.data); setUsers(u.data); setSettings(s.data); setRoleEligibility(r.data || {});
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor.toISOString()]);

  const filteredTours = tours.filter((t) =>
    (filterPosition === "ALL" || (t.position || t.title) === filterPosition) &&
    (filterCert === "ALL" || t.required_certification === filterCert)
  );

  const draftCount = filteredTours.filter((t) => t.published === false).length;
  const publishedCount = filteredTours.filter((t) => t.published !== false).length;

  async function publishWeek(published) {
    // Preserved for legacy references. Public callers should use
    // openPublishPreview() so the operator can review before publishing.
    if (!window.confirm(`${published ? "Publish" : "Unpublish"} all ${filteredTours.length} shifts in this week?`)) return;
    try {
      const { data } = await api.post("/tours/bulk-publish", {
        start: range.start.toISOString(), end: range.end.toISOString(), published,
      });
      toast.success(`${published ? "Published" : "Unpublished"} ${data.updated} shifts`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function clearWeek() { /* deprecated — replaced by Clear Range multi-week selector */ }
  const [publishPreview, setPublishPreview] = useState(null);
  function openPublishPreview() {
    setPublishPreview({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      tours: filteredTours,
      weekLabel: `${fmtMon(days[0])} – ${fmtMon(days[6])}`,
    });
  }
  function toGroup(t) {
    return { key: t.id, position: t.position || t.title, start_time: t.start_time, end_time: t.end_time, required_certification: t.required_certification, ambulance_role: t.ambulance_role || null, tours: [t] };
  }

  async function pickup(tour) {
    try { await api.post(`/tours/${tour.id}/pickup`); toast.success("Shift picked up"); setSelected(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Pickup failed"); }
  }
  async function requestDrop(tour) {
    const hoursUntilStart = (new Date(tour.start_time) - new Date()) / 3600000;
    // < 24h — show the mandated advisory copy. Frontend blocks the
    // one-tap release; the member must explicitly confirm to file a
    // supervisor-review request on the Drop Board.
    if (hoursUntilStart < 24) {
      const proceed = window.confirm(
        "Supervisor approval is required to drop a tour that starts in less than 24 hours. Please contact the on-duty Tour Supervisor or Tour Commander to request removal.\n\n" +
        "Press OK to also submit this on the Drop Board for supervisor review, or Cancel to reach out first."
      );
      if (!proceed) { setSelected(null); return; }
    }
    try {
      const { data } = await api.post(`/tours/${tour.id}/drop-request`, { reason: dropReason });
      const released = data?.status === "open";
      toast.success(released ? "Shift released — back in the open pool" : "Drop request submitted — waiting on supervisor");
      setDropReason(""); setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function approveDrop(tour) { try { await api.post(`/tours/${tour.id}/drop-approve`); toast.success("Drop approved"); setSelected(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function denyDrop(tour) { try { await api.post(`/tours/${tour.id}/drop-deny`); toast.success("Drop denied"); setSelected(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function deleteTour(tour) { if (!window.confirm("Delete this shift?")) return; await api.delete(`/tours/${tour.id}`); toast.success("Deleted"); setSelected(null); load(); }
  async function togglePublishTour(tour) {
    try { await api.patch(`/tours/${tour.id}`, { published: !tour.published }); toast.success(tour.published ? "Unpublished" : "Published"); setSelected(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function assignTour(tour, userId, overrideOpts) {
    // The backend answers 409 with `requires_override: true` when the
    // picked member fails the position's certification check. We
    // surface that as a confirmation dialog (only supervisor+ ever
    // gets here — the drawer only shows Assign for privileged users).
    const body = { assigned_user_id: userId };
    if (overrideOpts?.emergency_override) {
      body.emergency_override = true;
      body.override_reason = overrideOpts.reason || null;
    }
    try {
      const { data } = await api.patch(`/tours/${tour.id}`, body);
      toast.success(overrideOpts?.emergency_override ? "Assigned with emergency override" : "Assigned");
      setSelected((prev) => prev ? { ...prev, tours: prev.tours.map((x) => x.id === tour.id ? data : x) } : prev);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // FastAPI serialises structured detail as an object → prompt for
      // override if the caller has permission.
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.requires_override) {
        setOverridePrompt({
          tour,
          userId,
          message: detail.message || "This member does not meet the certification requirements for this position.",
          positionRole: detail.position_role,
          requiredCert: detail.required_cert,
        });
        return;
      }
      toast.error((typeof detail === "string" ? detail : detail?.message) || "Assign failed");
    }
  }
  async function unassignTour(tour) {
    if (!window.confirm(`Unassign ${tour.assigned_user_name || "member"} from this shift?`)) return;
    try {
      const { data } = await api.patch(`/tours/${tour.id}`, { assigned_user_id: "" });
      toast.success("Unassigned");
      setSelected((prev) => prev ? { ...prev, tours: prev.tours.map((x) => x.id === tour.id ? data : x) } : prev);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  const subtitle = `Week of ${fmtMon(days[0])} – ${fmtMon(days[6])}, ${days[0].getFullYear()}`;

  return (
    <AppLayout title="Schedule" subtitle={subtitle} actions={
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <div className="border border-slate-200 rounded-sm overflow-hidden flex">
          <button onClick={() => setView("position")} className={`px-3 py-1.5 text-xs font-semibold ${view === "position" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid="view-position">POSITION VIEW</button>
          <button onClick={() => setView("open")} className={`px-3 py-1.5 text-xs font-semibold ${view === "open" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid="view-open">OPEN SHIFTS</button>
          <button onClick={() => setView("mine")} className={`px-3 py-1.5 text-xs font-semibold ${view === "mine" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid="view-mine">MY SCHEDULE</button>
        </div>
        <Button variant="outline" size="icon" onClick={() => setCursor(addDays(cursor, -7))} data-testid="nav-prev" className="rounded-sm"><CaretLeft size={14} weight="bold" /></Button>
        <Button variant="outline" onClick={() => setCursor(startOfWeek(new Date()))} className="rounded-sm h-9 text-xs" data-testid="nav-today">THIS WEEK</Button>
        <Button variant="outline" size="icon" onClick={() => setCursor(addDays(cursor, 7))} data-testid="nav-next" className="rounded-sm"><CaretRight size={14} weight="bold" /></Button>
        <Input
          type="date"
          data-testid="nav-date"
          className="rounded-sm h-9 w-36 mono text-xs"
          value={isoDatePart(cursor)}
          onChange={(e) => { if (e.target.value) setCursor(startOfWeek(e.target.value)); }}
          title="Jump to the week containing this date"
        />
        <Button variant="outline" onClick={() => window.print()} className="rounded-sm h-9 text-xs" data-testid="print-schedule"><Printer size={14} weight="bold" className="mr-1" />PRINT</Button>
        {isSup && (
          <>
            <ScheduleHealthButton health={health} onClick={() => setOpenHealth(true)} />
            <Button variant="outline" onClick={() => setOpenUpload(true)} className="rounded-sm h-9 text-xs" data-testid="btn-upload-template"><UploadSimple size={14} weight="bold" className="mr-1" />UPLOAD TEMPLATE</Button>
            <Button variant="outline" onClick={() => setOpenBulkClear(true)} className="rounded-sm h-9 text-xs border-red-600 text-red-800 hover:bg-red-50" data-testid="btn-bulk-clear"><Trash size={14} weight="bold" className="mr-1" />CLEAR RANGE</Button>
            <Button variant="outline" onClick={() => setOpenSave(true)} className="rounded-sm h-9 text-xs" data-testid="btn-save-template"><FloppyDisk size={14} weight="bold" className="mr-1" />SAVE TEMPLATE</Button>
            <Button variant="outline" onClick={openPublishPreview} className="rounded-sm h-9 text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid="btn-publish-week"><Broadcast size={14} weight="bold" className="mr-1" />PUBLISH WEEKS</Button>
            <Button onClick={() => setOpenCreate(true)} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-create-tour"><Plus size={14} weight="bold" /> Post Shift</Button>
          </>
        )}
      </div>
    }>
      <div className="surface-grid px-3 py-2 mb-3 flex items-center gap-4 flex-wrap text-xs" data-testid="schedule-legend">
        <span className="tab-tag text-slate-500 flex items-center gap-1"><Funnel size={12} weight="bold" /> Legend</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-700 rounded-sm" /> My shift</span>
        <span className="flex items-center gap-1.5"><span className="inline-block px-1 py-0.5 bg-yellow-300 text-yellow-900 text-[9px] font-bold rounded-sm">UNASSIGNED</span> Open</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-blue-700 rounded-sm" /> Assigned</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-amber-500 rounded-sm" /> Drop pending</span>
        <div className="ml-auto flex items-center gap-2">
          <select value={filterPosition} onChange={(e) => setFilterPosition(e.target.value)} className="border border-slate-200 rounded-sm h-8 px-2 text-xs" data-testid="filter-position">
            <option value="ALL">All positions</option>{positions.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select value={filterCert} onChange={(e) => setFilterCert(e.target.value)} className="border border-slate-200 rounded-sm h-8 px-2 text-xs" data-testid="filter-cert">
            <option value="ALL">All certs</option>{certCatalog.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {isSup && draftCount > 0 && (
        <div className="surface-grid border-amber-300 bg-amber-50 px-3 py-2 mb-3 flex items-center gap-3" data-testid="draft-banner">
          <EyeSlash size={16} weight="bold" className="text-amber-700" />
          <div className="text-sm text-amber-900 flex-1">
            <strong>{draftCount}</strong> draft shift(s) hidden from volunteers · {publishedCount} published. Use <strong>PUBLISH WEEKS</strong> to release them.
          </div>
        </div>
      )}

      {view === "position" && (
        <PositionGrid
          positions={positions}
          days={days}
          tours={filteredTours}
          currentUser={user}
          onSelectGroup={setSelected}
          onEditBucket={(bucket) => {
            // Bulk-edit the entire time bucket. We seed the EditShiftDialog
            // with the first tour and stash every tour id in the bucket so
            // save can PATCH them all with the same time/position/description.
            const firstTour = bucket.groups?.[0]?.tours?.[0];
            if (!firstTour) return;
            const bucketTourIds = bucket.groups.flatMap((g) => (g.tours || []).map((t) => t.id));
            setEditing({ ...firstTour, __bucketTourIds: bucketTourIds });
          }}
          onDeleteBucket={(bucket) => setBucketToDelete(bucket)}
        />
      )}
      {view === "open" && <OpenShiftsBoard tours={filteredTours} currentUser={user} roleEligibility={roleEligibility} onSelect={(t) => setSelected(toGroup(t))} />}
      {view === "mine" && <MyScheduleList tours={filteredTours} currentUser={user} onSelect={(t) => setSelected(toGroup(t))} />}

      <PostShiftDialog open={openCreate} onOpenChange={setOpenCreate} positions={positions} certCatalog={certCatalog} users={users} roleEligibility={roleEligibility} defaultDate={isoDatePart(days[0])} onCreated={load} />
      <EditShiftDialog tour={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} positions={positions} certCatalog={certCatalog} roleEligibility={roleEligibility} onSaved={() => { setEditing(null); setSelected(null); load(); }} />
      <TradeRequestDialog tour={tradeTour} open={!!tradeTour} onOpenChange={(o) => !o && setTradeTour(null)} onRequested={() => { setTradeTour(null); load(); }} />
      <SaveTemplateDialog open={openSave} onOpenChange={setOpenSave} tours={filteredTours} weekStart={range.start} onSaved={() => toast.success("Template saved — available in Upload Template menu")} />
      <UploadTemplateDialog open={openUpload} onOpenChange={setOpenUpload} weekStart={range.start} onApplied={() => { load(); health.refresh(); }} />
      <BulkClearDialog open={openBulkClear} onOpenChange={setOpenBulkClear} onCleared={() => { load(); health.refresh(); }} />
      {isSup && <ScheduleHealthDialog open={openHealth} onOpenChange={setOpenHealth} health={health} onChanged={load} />}

      <AlertDialog open={!!bucketToDelete} onOpenChange={(o) => !o && setBucketToDelete(null)}>
        <AlertDialogContent data-testid="bucket-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the entire tour?</AlertDialogTitle>
            <AlertDialogDescription>
              {bucketToDelete && (() => {
                const ids = (bucketToDelete.groups || []).flatMap((g) => (g.tours || []).map((t) => t.id));
                const first = bucketToDelete.groups?.[0]?.tours?.[0];
                const pos = first?.position || first?.title || "this tour";
                return (
                  <>
                    This will remove <strong>{ids.length}</strong> slot{ids.length === 1 ? "" : "s"} for <strong className="uppercase">{pos}</strong> at{" "}
                    <span className="mono">{fmtTime24(bucketToDelete.start_time)}–{fmtTime24(bucketToDelete.end_time)}</span>.
                    Any members currently assigned will be unassigned. This cannot be undone.
                  </>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bucketDeleting} data-testid="bucket-delete-cancel">No, keep it</AlertDialogCancel>
            <AlertDialogAction
              disabled={bucketDeleting}
              data-testid="bucket-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async (e) => {
                e.preventDefault();
                if (!bucketToDelete) return;
                const ids = (bucketToDelete.groups || []).flatMap((g) => (g.tours || []).map((t) => t.id));
                setBucketDeleting(true);
                let ok = 0, fail = 0;
                for (const tid of ids) {
                  try { await api.delete(`/tours/${tid}`); ok += 1; } catch { fail += 1; }
                }
                setBucketDeleting(false);
                setBucketToDelete(null);
                if (ok > 0) toast.success(`Tour deleted — ${ok} slot${ok === 1 ? "" : "s"} removed${fail ? ` (${fail} failed)` : ""}`);
                if (ok === 0) toast.error("Delete failed");
                load();
              }}
            >
              {bucketDeleting ? "Deleting…" : "Yes, delete tour"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-[460px] sm:max-w-[480px] overflow-y-auto" data-testid="tour-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display tracking-tight">{selected.position || "Shift"}</SheetTitle>
                <div className="text-xs text-slate-500 mono">{fmtDateLong(new Date(selected.start_time))}</div>
                <div className="text-xs text-slate-500 mono">{fmtTime24(selected.start_time)} – {fmtTime24(selected.end_time)} · {tourHours(selected).toFixed(1)}h per slot</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <Badge className={`rounded-sm ${CERT_COLOR[selected.required_certification] || "bg-slate-700 text-white"}`}>{selected.required_certification}</Badge>
                  {selected.ambulance_role && <Badge className={`rounded-sm ${ROLE_COLOR[selected.ambulance_role] || "bg-slate-700 text-white"}`}>Role: {roleLabel(selected.ambulance_role, selected.position)}</Badge>}
                  <span className="tab-tag text-slate-500">{selected.tours.length} slot(s)</span>
                </div>
              </SheetHeader>

              <div className="mt-4 space-y-3">
                {selected.tours.map((t, idx) => {
                  const isMine = t.assigned_user_id === user?.id;
                  const isOpen = !t.assigned_user_id && t.status === "open";
                  const dropPending = t.status === "drop_pending";
                  const draft = t.published === false;
                  return (
                    <div key={t.id} className={`border rounded-sm p-3 ${isMine ? "border-emerald-400 bg-emerald-50" : draft ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`drawer-slot-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="tab-tag text-slate-500">Slot {idx + 1}</div>
                        <div className="flex items-center gap-1">
                          {draft && <Badge className="rounded-sm bg-amber-200 text-amber-900 text-[10px]">DRAFT</Badge>}
                          <Badge className="rounded-sm bg-slate-700 text-white text-[10px]">{t.status.toUpperCase()}</Badge>
                        </div>
                      </div>
                      <div className="text-sm">
                        <div className="font-medium">
                          {t.assigned_user_name ? <>{t.assigned_user_name}{t.assigned_user_clearance && <span className="ml-1 mono text-[11px] text-slate-600" data-testid={`clearance-${t.id}`}>({t.assigned_user_clearance})</span>}{t.assigned_user_cert && <Badge className={`ml-2 rounded-sm ${CERT_COLOR[t.assigned_user_cert] || "bg-slate-700 text-white"}`}>{t.assigned_user_cert}</Badge>}{isMine && <span className="ml-2 text-emerald-700 font-bold">★ YOU</span>}</> : <span className="text-slate-500 italic">Unassigned</span>}
                        </div>
                        {t.drop_request && (
                          <div className="mt-1 text-xs border border-amber-300 bg-white p-1.5 rounded-sm">
                            <span className="tab-tag text-amber-700">Drop · {t.drop_request.status}</span>
                            <span className="ml-1">{t.drop_request.reason || "—"}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 space-y-2">
                        {/* ===== Volunteer pickup — role precedence matches backend _can_pickup ===== */}
                        {(() => {
                          if (!isOpen) return null;
                          if (draft && !isSup) return <div className="text-[11px] text-amber-700 italic">Draft — not yet published.</div>;
                          if (new Date(t.start_time) <= new Date()) {
                            return (
                              <div className="space-y-1" data-testid={`past-shift-${idx}`}>
                                <Button onClick={() => toast.error(PAST_PICKUP_MSG)} size="sm" variant="outline" className="w-full rounded-sm border-slate-300 text-slate-500 bg-slate-50 cursor-not-allowed" data-testid={`btn-pickup-past-${idx}`}>Past shift — pickup closed</Button>
                                <div className="text-[11px] text-slate-600">{PAST_PICKUP_MSG}</div>
                              </div>
                            );
                          }
                          // iter25 — When the shift has an ambulance_role,
                          // the role is the authoritative gate (same rule
                          // the backend applies). The `required_certification`
                          // string is a display hint and may be a combined
                          // label like "EMT/Paramedic" that the simple
                          // string matcher cannot parse — don't block on it.
                          const roleOK = !t.ambulance_role || userEligibleForRole(user, t.ambulance_role, roleEligibility);
                          const certOK = t.ambulance_role ? true : userIsAuthorized(user, t.required_certification);
                          if (certOK && roleOK) {
                            return <Button onClick={() => pickup(t)} size="sm" className="w-full rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`btn-pickup-${idx}`}>Pick Up This Slot</Button>;
                          }
                          const reasons = [];
                          if (!roleOK) reasons.push(`Not eligible for ${roleLabel(t.ambulance_role, t.position)}`);
                          if (!certOK) reasons.push(`Not authorized for ${t.required_certification}`);
                          // iter29 — Show the three-axis snapshot so a
                          // member can see immediately if their profile is
                          // missing a clearance (common cause of "I'm a
                          // cleared MVO but the button says I'm not").
                          const vehicles = Array.isArray(user?.cleared_to_drive) ? user.cleared_to_drive.filter((v) => String(v || "").trim()) : [];
                          const driverLabel = (user?.driver_clearance_date || "").toString().trim() || (vehicles.length ? `${vehicles.length} vehicle${vehicles.length === 1 ? "" : "s"}` : "not cleared");
                          return (
                            <div className="border border-slate-200 bg-slate-50 p-1.5 rounded-sm text-[11px] text-slate-600" data-testid={`pickup-blocked-${idx}`}>
                              <div className="flex items-center gap-1.5"><Lock size={12} /> {reasons.join(" · ")}.</div>
                              <div className="mt-1 text-[10px] text-slate-500 font-mono">Clinical: {user?.clearance_status || "N"} · Driver: {driverLabel} · Dispatcher: {user?.dispatcher_status || "—"}</div>
                            </div>
                          );
                        })()}

                        {/* ===== Supervisor / Admin: direct assignment ===== */}
                        {isSup && !dropPending && (
                          <AssignControl
                            tour={t}
                            users={users}
                            roleEligibility={roleEligibility}
                            onAssign={(uid) => assignTour(t, uid)}
                            onUnassign={() => unassignTour(t)}
                            idx={idx}
                          />
                        )}

                        {isMine && t.status === "assigned" && new Date(t.start_time) <= new Date() && (
                          <div className="text-[11px] p-1.5 rounded-sm border border-slate-300 bg-slate-50 text-slate-700" data-testid={`past-mine-${idx}`}>This tour has already started — drops and trades are closed. Contact a supervisor for changes.</div>
                        )}
                        {isMine && t.status === "assigned" && new Date(t.start_time) > new Date() && (() => {
                          const hoursUntilStart = (new Date(t.start_time) - new Date()) / 3600000;
                          const autoRelease = hoursUntilStart >= 24;
                          return (
                            <>
                              <Label className="tab-tag">{autoRelease ? "Release reason (optional)" : "Drop request reason"}</Label>
                              <Textarea data-testid={`drop-reason-${idx}`} className="rounded-sm" value={dropReason} onChange={(e) => setDropReason(e.target.value)} />
                              <div className={`text-[11px] p-1.5 rounded-sm border ${autoRelease ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-900"}`}>
                                {autoRelease
                                  ? `Starts in ${Math.round(hoursUntilStart)}h — you can release this shift immediately. No supervisor approval needed.`
                                  : "Supervisor approval is required to drop a tour that starts in less than 24 hours. Please contact the on-duty Tour Supervisor or Tour Commander to request removal."}
                              </div>
                              <Button onClick={() => requestDrop(t)} size="sm" variant="outline" className={`w-full rounded-sm ${autoRelease ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50" : "border-red-400 text-red-700 hover:bg-red-50"}`} data-testid={`btn-drop-req-${idx}`}>
                                {autoRelease ? "Release Shift" : "Submit Drop Request for Supervisor"}
                              </Button>
                              <Button onClick={() => { setSelected(null); setTradeTour(t); }} size="sm" variant="outline" className="w-full rounded-sm border-blue-400 text-blue-700 hover:bg-blue-50" data-testid={`btn-trade-req-${idx}`}>
                                <ArrowsLeftRight size={12} weight="bold" className="mr-1" />Request Trade
                              </Button>
                            </>
                          );
                        })()}
                        {t.status === "trade_pending" && t.trade_request && (
                          <div className="border border-blue-300 bg-blue-50 p-2 rounded-sm text-[11px] text-blue-900">
                            <span className="tab-tag text-blue-700">Trade Pending</span>
                            <div className="mt-1">{t.trade_request.requested_by_name} ↔ {t.trade_request.target_user_name}</div>
                            {t.trade_request.reason && <div className="italic mt-1">"{t.trade_request.reason}"</div>}
                          </div>
                        )}
                        {isSup && dropPending && (
                          <div className="grid grid-cols-2 gap-2">
                            <Button onClick={() => approveDrop(t)} size="sm" className="rounded-sm bg-emerald-700 text-white" data-testid={`btn-drop-approve-${idx}`}>Approve Drop</Button>
                            <Button onClick={() => denyDrop(t)} size="sm" variant="outline" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50" data-testid={`btn-drop-deny-${idx}`}>Deny</Button>
                          </div>
                        )}
                        {isSup && (
                          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200">
                            <Button onClick={() => setEditing(t)} size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" data-testid={`btn-edit-${idx}`}>
                              <PencilSimple size={12} className="mr-1" />Edit
                            </Button>
                            <Button onClick={() => deleteTour(t)} size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-red-600 hover:bg-red-50" data-testid={`btn-delete-${idx}`}><Trash size={12} className="mr-1" />Delete</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <PublishWeeksDialog
        open={!!publishPreview}
        viewedWeekStart={etDayKey(days[0].toISOString())}
        viewedTours={publishPreview?.tours || []}
        weekLabel={publishPreview?.weekLabel}
        onClose={() => setPublishPreview(null)}
        onDone={() => { load(); health.refresh(); }}
      />
      <EmergencyOverrideDialog
        prompt={overridePrompt}
        onCancel={() => setOverridePrompt(null)}
        onConfirm={(reason) => {
          const { tour, userId } = overridePrompt;
          setOverridePrompt(null);
          assignTour(tour, userId, { emergency_override: true, reason });
        }}
      />
    </AppLayout>
  );
}

/* Emergency Override — shown when an authorised user assigns a member
 * who does not meet a position's certification requirements. Applies
 * only to THIS assignment; the member's certifications and future
 * eligibility are untouched. */
function EmergencyOverrideDialog({ prompt, onCancel, onConfirm }) {
  const [reason, setReason] = React.useState("");
  React.useEffect(() => { setReason(""); }, [prompt]);
  if (!prompt) return null;
  return (
    <Dialog open={!!prompt} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="emergency-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={18} weight="fill" /> Emergency Override Required
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-sm text-slate-700">{prompt.message}</p>
          <p className="text-sm text-slate-700">Confirm an emergency override to proceed.</p>
          {(prompt.positionRole || prompt.requiredCert) && (
            <div className="text-xs text-slate-500 mono bg-slate-50 border border-slate-200 rounded-sm p-2">
              {prompt.positionRole && <div>Position role: <strong>{prompt.positionRole}</strong></div>}
              {prompt.requiredCert && <div>Required certification: <strong>{prompt.requiredCert}</strong></div>}
            </div>
          )}
          <div className="space-y-1">
            <Label className="tab-tag">Reason for Override (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Same-day gap — no eligible member available"
              className="rounded-sm h-20"
              data-testid="emergency-override-reason"
            />
            <p className="text-[10px] text-slate-500">This override applies only to this assignment. The member's certifications and future eligibility are unchanged.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="emergency-override-cancel">Cancel</Button>
          <Button
            className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
            onClick={() => onConfirm(reason)}
            data-testid="emergency-override-confirm"
          >
            Confirm Emergency Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
